import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useApolloClient } from '@apollo/client';
import { Helmet } from 'react-helmet';
import moment from 'moment';
import { Alert, Button, IconButton, Tooltip, Grid, CircularProgress, Box, Tabs, Tab, Typography, Divider, Switch, FormControlLabel } from '@mui/material';

import { useQuery, useMutation } from '@apollo/client';
import i18next from 'i18next';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import SingleBedIcon from '@mui/icons-material/SingleBed';
import KingBedIcon from '@mui/icons-material/KingBed';
import CoffeeIcon from '@mui/icons-material/Coffee';
import FreeBreakfastIcon from '@mui/icons-material/FreeBreakfast';
import LunchDiningIcon from '@mui/icons-material/LunchDining';
import DinnerDiningIcon from '@mui/icons-material/DinnerDining';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocalCafeIcon from '@mui/icons-material/LocalCafe';
import RoomServiceIcon from '@mui/icons-material/RoomService';
import VideocamIcon from '@mui/icons-material/Videocam';
import TvIcon from '@mui/icons-material/Tv';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PushPinIcon from '@mui/icons-material/PushPin';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import StarIcon from '@mui/icons-material/Star';

import { useForm, useFieldArray, FormProvider, useFormContext } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import yup from 'validation';

import { dispatchException, dispatchMessage } from 'helper/snackbar';
import { showValidationAlert } from 'helper/form';

import { FormInputText } from 'components/form/FormInputText';
import { FormInputNumber } from 'components/form/FormInputNumber';
import { FormInputDate } from 'components/form/FormInputDate';
import { FormInputCheckbox } from 'components/form/FormInputCheckbox';
import { FormInputRadio } from 'components/form/FormInputRadio';
import { FormInputCurrency } from 'components/form/FormInputCurrency';
import { FormInputDropdown } from 'components/form/FormInputDropdown';
import { FormInputMultiCheckbox } from 'components/form/FormInputMultiCheckbox';
import { UnsavedChangesPrompt } from 'components/form/UnsavedChangesPrompt';
import CustomTabs from 'components/Tabs';
import SimpleTable from 'components/table/SimpleTable';
import ConfirmationButton from 'components/dialogs/ConfirmationButton';
import ScrollToHashElement from 'components/ScrollToHashElement';
import { bookingModeSelector, filterSelector } from 'helper/filter';
import { userSelector, canEditAdminRecord, canEditAdminSpaceId, filterForSelectableRecords } from 'helper/security';

import {
  PRICESELECTOR_VIEW_QUERY,
  PRICESELECTORBASE_PRICES_VIEW_QUERY,
  PRICELIST_VIEW_QUERY,
  TAXTYPES_LIST_FULL_QUERY,
  UPDATE_PRICESELECTOR_MUTATION,
  CREATE_PRICESELECTOR_MUTATION,
  DELETE_PRICESELECTOR_MUTATION,
  COPY_PRICESELECTOR_MUTATION,
  REFETCH_PRICESELECTORS_QUERIES,
  EVICT_PRICESELECTORS_QUERIES,
} from './gql';
import { PRODUCTBUNDLES_COMPONENTS_QUERY, FACILITIES_COMPONENTS_QUERY, PRODUCTS_COMPONENTS_QUERY } from '../products/gql';
import { PUBLIC_COMPANY_LIST_QUERY } from '../clients/gql';

import {
  PriceSelectorListOutput,
  ProductBundleListOutput,
  FacilityListOutput,
  ProductListOutput,
  PriceListListOutput,
  TaxTypeListOutput,
  EPricingWeekDays,
  EProductLookupCode,
  EFacilityLookupCode,
  EFacilityPayment,
} from '__generated__/graphql';
import { formatTaxTypeName } from 'components/tax/TaxComponentSelectionInput';
import { formatPrice } from 'components/Price';
import { RedirectError } from 'pages/error';
import { formatDocumentTitle } from 'helper/usedocumenttitle';
import { EBookingMode } from 'types';

interface PriceSelectorProps {
  priceListId: number;
  id: number;
  spaceId?: number;
  forLister?: boolean;
  inline?: boolean;
  onNavigateToSelector?: (id: number) => void;
  onDeleted?: () => void;
}
interface PriceSelectorCreateProps {
  priceListId: number;
  spaceId?: number;
  forLister?: boolean;
  priceList?: PriceListListOutput;
  inline?: boolean;
  onNavigateToSelector?: (id: number) => void;
  onDeleted?: () => void;
}
interface PriceSelectorFormProps {
  data: PriceSelectorListOutput;
  baseSelector: PriceSelectorListOutput;
  bundles: ProductBundleListOutput[];
  facilities: FacilityListOutput[];
  products: ProductListOutput[];
  taxTypes: TaxTypeListOutput[];
  publicCompanies?: any[];
  inline?: boolean;
  onNavigateToSelector?: (id: number) => void;
  onDeleted?: () => void;
}

interface ListerPriceTabConfig {
  key: string;
  label: string;
  content: React.ReactNode;
  hasError?: boolean;
}

interface ListerPriceTabsProps {
  tabs: ListerPriceTabConfig[];
  initialIndex?: number;
}

function ListerPriceTabs({ tabs, initialIndex = 0 }: ListerPriceTabsProps) {
  const safeInitialIndex = initialIndex >= 0 && initialIndex < tabs.length ? initialIndex : 0;
  const [activeTab, setActiveTab] = useState(safeInitialIndex);

  useEffect(() => {
    setActiveTab(safeInitialIndex);
  }, [safeInitialIndex]);

  const activeTabConfig = tabs[activeTab] ?? null;

  if (tabs.length === 0) return null;

  return (
    <Box>
      <Tabs value={activeTab} onChange={(_event, value: number) => setActiveTab(value)} aria-label={String(i18next.t('selector-price-tabs-aria'))}>
        {tabs.map(tab => (
          <Tab key={tab.key} label={tab.hasError ? `${tab.label} *` : tab.label} />
        ))}
      </Tabs>
      <Box>
        {activeTabConfig?.content ?? (
          <Typography variant="body2">{i18next.t('selector-lister-empty-default')}</Typography>
        )}
      </Box>
    </Box>
  );
}

const componentsRequiredSchema = yup.object().shape({
  taxTypeId: yup.number().required(),
  price: yup.number().required().label(i18next.t('selector-multitax-price')),
});

const componentsOptionalSchema = yup.object().shape({
  taxTypeId: yup.number().required(),
  price: yup.number().nullable(),
});

const productsSchema = yup.object().shape({
  linkId: yup.number().required(),
  linkSpace: yup.string().required(),
  linkSpaceId: yup.number().required(),
  name: yup.string(),
  sku: yup.string(),
  variant: yup.mixed<'classic' | 'premium'>().oneOf(['classic', 'premium']).required(),
  lookupCode: yup.mixed<EProductLookupCode>().nullable(),
  isPremium: yup.boolean().required(),
  price: yup
    .number()
    .nullable()
    .when(
      ['lookupCode', 'isPremium', 'linkSpaceId', 'multiTax', '$isBasePrice', '$selectorSpaceId', '$isEpiloPricelist', '$vollpensionEzEnabled', '$vollpensionDzEnabled'],
      ([lookupCode, isPremium, linkSpaceId, multiTax, isBasePrice, selectorSpaceId, isEpiloPricelist, vollpensionEzEnabled, vollpensionDzEnabled], schema) => {
        // For base prices, require all prices EXCEPT:
        // - Premium variants (optional for normal pricelists, required for Epilo)
        // - Combination products (Vollpensionspauschalen) - UNLESS their switch is enabled
        // - Products from other spaces (only validate current space)
        // - Multi-tax products (price is calculated from components, not directly entered)
        /*
        if (isBasePrice && linkSpaceId === selectorSpaceId && !multiTax) {
          const isCombination =
            lookupCode === EProductLookupCode.VOLLPENSIONSPAUSCHALE_EZ || lookupCode === EProductLookupCode.VOLLPENSIONSPAUSCHALE_DZ;

          // Check if this combination product's switch is enabled
          const isEzWithSwitch = lookupCode === EProductLookupCode.VOLLPENSIONSPAUSCHALE_EZ && vollpensionEzEnabled;
          const isDzWithSwitch = lookupCode === EProductLookupCode.VOLLPENSIONSPAUSCHALE_DZ && vollpensionDzEnabled;

          // For Epilo pricelists, validate both classic and premium variants for lister products
          if (isEpiloPricelist) {
            const isListerProduct =
              lookupCode === EProductLookupCode.ROOM_EZ ||
              lookupCode === EProductLookupCode.ROOM_DZ ||
              lookupCode === EProductLookupCode.ADDON_COFFEE ||
              lookupCode === EProductLookupCode.ADDON_COFFEEBREAK ||
              lookupCode === EProductLookupCode.ADDON_LUNCH ||
              lookupCode === EProductLookupCode.ADDON_DINNER;

            // Require prices for lister products OR combination products with enabled switches
            if (isListerProduct || isEzWithSwitch || isDzWithSwitch) {
              return schema.required().label(i18next.t('selector-product-price'));
            }
          } else if (!isPremium && (!isCombination || isEzWithSwitch || isDzWithSwitch)) {
            // For normal pricelists, require classic variants (premium optional)
            // For combinations, only require if switch is enabled
            return schema.required().label(i18next.t('selector-product-price'));
          }
        }
        */
        return schema;
      },
    ),
  multiTax: yup.boolean().required(),
  singleTaxTypeId: yup.number().nullable(),
  components: yup
    .array()
    .required()
    .when(
      ['multiTax', 'price', 'lookupCode', 'isPremium', 'linkSpaceId', '$isBasePrice', '$selectorSpaceId', '$isEpiloPricelist', '$vollpensionEzEnabled', '$vollpensionDzEnabled'],
      ([multiTax, price, lookupCode, isPremium, linkSpaceId, isBasePrice, selectorSpaceId, isEpiloPricelist, vollpensionEzEnabled, vollpensionDzEnabled], schema) => {
        // For base prices, always require component prices if multiTax (except for premium variants on normal pricelists and combinations WITHOUT enabled switch)
        /*
        if (isBasePrice && multiTax && linkSpaceId === selectorSpaceId) {
          const isCombination =
            lookupCode === EProductLookupCode.VOLLPENSIONSPAUSCHALE_EZ || lookupCode === EProductLookupCode.VOLLPENSIONSPAUSCHALE_DZ;

          // Check if this combination product's switch is enabled
          const isEzWithSwitch = lookupCode === EProductLookupCode.VOLLPENSIONSPAUSCHALE_EZ && vollpensionEzEnabled;
          const isDzWithSwitch = lookupCode === EProductLookupCode.VOLLPENSIONSPAUSCHALE_DZ && vollpensionDzEnabled;

          // For Epilo pricelists, validate both classic and premium for lister products
          if (isEpiloPricelist) {
            const isListerProduct =
              lookupCode === EProductLookupCode.ROOM_EZ ||
              lookupCode === EProductLookupCode.ROOM_DZ ||
              lookupCode === EProductLookupCode.ADDON_COFFEE ||
              lookupCode === EProductLookupCode.ADDON_COFFEEBREAK ||
              lookupCode === EProductLookupCode.ADDON_LUNCH ||
              lookupCode === EProductLookupCode.ADDON_DINNER;
            // Require for lister products OR combination products with enabled switches
            if (isListerProduct || isEzWithSwitch || isDzWithSwitch) {
              return schema.of(componentsRequiredSchema).required();
            }
          } else if (!isPremium && (!isCombination || isEzWithSwitch || isDzWithSwitch)) {
            // For normal pricelists, require classic components (premium optional)
            // For combinations, only require if switch is enabled
            return schema.of(componentsRequiredSchema).required();
          }
        }
        // For non-base prices, require components only if price is set
        if (multiTax && price) {
          return schema.of(componentsRequiredSchema).required();
        } else {
          return schema.of(componentsOptionalSchema);
        }
        */
        return schema.of(componentsOptionalSchema);
      },
    ),
});

type CollectionType = 'productsPrices' | 'bundlesPrices' | 'facilitiesPrices';

enum EBundleMode {
  NONE = 'NONE',
  PRODUCTS = 'PRODUCTS',
  PRICE = 'PRICE',
}

const bundlesSchema = yup.object().shape({
  linkId: yup.number().required(),
  linkSpace: yup.string().required(),
  linkSpaceId: yup.number().required(),
  name: yup.string(),
  sku: yup.string(),
  variant: yup.mixed<'classic' | 'premium'>().oneOf(['classic', 'premium']).required(),
  lookupCode: yup.mixed<EProductLookupCode>().nullable(),
  isPremium: yup.boolean().required(),
  price: yup
    .number()
    .nullable()
    .when(
      ['mode', 'isPremium', 'linkSpaceId', 'multiTax', '$isBasePrice', '$selectorSpaceId', '$isEpiloPricelist'],
      ([mode, isPremium, linkSpaceId, multiTax, isBasePrice, selectorSpaceId, isEpiloPricelist], schema) => {
        // For base prices with PRICE mode, require price (except for premium variants on normal pricelists, other spaces, and multi-tax)
        // For multi-tax bundles, price is calculated from components
        if (isBasePrice && mode === EBundleMode.PRICE && linkSpaceId === selectorSpaceId && !multiTax) {
          // For Epilo pricelists, require both classic and premium
          if (isEpiloPricelist || !isPremium) {
            return schema.required().label(i18next.t('selector-bundle-price'));
          }
        }
        return schema;
      },
    ),
  multiTax: yup.boolean().required(),
  singleTaxTypeId: yup.number().nullable(),
  mode: yup.string().required().oneOf(Object.keys(EBundleMode)),
  serviceTypeAssignments: yup
    .array()
    .of(
      yup.object().shape({
        serviceTypeSku: yup.string().required(),
        listerAssignment: yup.string().nullable(),
      }),
    )
    .optional(),
  components: yup
    .array()
    .required()
    .when(
      ['multiTax', 'mode', 'price', 'isPremium', 'linkSpaceId', '$isBasePrice', '$selectorSpaceId', '$isEpiloPricelist'],
      ([multiTax, mode, price, isPremium, linkSpaceId, isBasePrice, selectorSpaceId, isEpiloPricelist], schema, options) => {
        // For base prices with PRICE mode and multiTax, always require components (except for premium variants on normal pricelists and other spaces)
        /*
        if (isBasePrice && mode === EBundleMode.PRICE && multiTax && linkSpaceId === selectorSpaceId) {
          // For Epilo pricelists, require both classic and premium
          if (isEpiloPricelist || !isPremium) {
            return schema.of(componentsRequiredSchema).required();
          }
        }
        // For both normal and Epilo pricelists, require components only when:
        // 1. multiTax is true AND
        // 2. mode is PRICE AND
        // 3. price is actually set (not null/undefined)
        if (multiTax && mode === 'PRICE' && price !== null && price !== undefined) {
          return schema.of(componentsRequiredSchema).required();
        } else {
          return schema.of(componentsOptionalSchema);
        }
        */
        return schema.of(componentsOptionalSchema);
      },
    ),
});
const facilitiesSchema = yup.object().shape({
  linkId: yup.number().required(),
  linkSpace: yup.string().required(),
  linkSpaceId: yup.number().required(),
  name: yup.string(),
  sku: yup.string(),
  variant: yup.mixed<'classic' | 'premium'>().oneOf(['classic', 'premium']).required(),
  lookupCode: yup.mixed<EFacilityLookupCode>().nullable(),
  recurring: yup.mixed<EFacilityPayment>().required(),
  isAddon: yup.boolean().required(),
  isPremium: yup.boolean().required(),
  price: yup
    .number()
    .nullable()
    .when(
      [
        'isPremium',
        'linkSpaceId',
        'linkId',
        'multiTax',
        'lookupCode',
        '$isBasePrice',
        '$selectorSpaceId',
        '$isEpiloPricelist',
        '$meetingRoomFacilityIds',
      ],
      ([isPremium, linkSpaceId, linkId, multiTax, lookupCode, isBasePrice, selectorSpaceId, isEpiloPricelist, meetingRoomFacilityIds], schema) => {
        // For base prices, require all prices (except for premium variants on normal pricelists, other spaces, and multi-tax)
        // For multi-tax facilities, price is calculated from components
        /*
        if (isBasePrice && linkSpaceId === selectorSpaceId && !multiTax) {
          // For Epilo pricelists, only validate addon facilities and meeting room facilities
          if (isEpiloPricelist) {
            // Addon facilities (equipment) are always required
            const isAddonFacility =
              lookupCode === EFacilityLookupCode.BEAMER ||
              lookupCode === EFacilityLookupCode.FLIPCHART ||
              lookupCode === EFacilityLookupCode.PINNWAND ||
              lookupCode === EFacilityLookupCode.MODERATION;
            // Meeting room facilities (actual hotel rooms) are required if they're in the hotel's meeting room list
            const isMeetingRoomFacility = meetingRoomFacilityIds && Array.isArray(meetingRoomFacilityIds) && meetingRoomFacilityIds.includes(linkId);
            if (isAddonFacility || isMeetingRoomFacility) {
              return schema.required().label(i18next.t('selector-facility-price'));
            }
          } else if (!isPremium) {
            // For normal pricelists, require all classic facilities
            return schema.required().label(i18next.t('selector-facility-price'));
          }
        }
        */
        return schema;
      },
    ),
  multiTax: yup.boolean().required(),
  singleTaxTypeId: yup.number().nullable(),
  components: yup
    .array()
    .required()
    .when(
      [
        'multiTax',
        'price',
        'isPremium',
        'linkSpaceId',
        'linkId',
        'lookupCode',
        '$isBasePrice',
        '$selectorSpaceId',
        '$isEpiloPricelist',
        '$meetingRoomFacilityIds',
      ],
      (
        [multiTax, price, isPremium, linkSpaceId, linkId, lookupCode, isBasePrice, selectorSpaceId, isEpiloPricelist, meetingRoomFacilityIds],
        schema,
      ) => {
        // For base prices, always require component prices if multiTax (except for premium variants on normal pricelists and other spaces)
        /*
        if (isBasePrice && multiTax && linkSpaceId === selectorSpaceId) {
          // For Epilo pricelists, only validate addon facilities and meeting room facilities
          if (isEpiloPricelist) {
            // Addon facilities (equipment) are always required
            const isAddonFacility =
              lookupCode === EFacilityLookupCode.BEAMER ||
              lookupCode === EFacilityLookupCode.FLIPCHART ||
              lookupCode === EFacilityLookupCode.PINNWAND ||
              lookupCode === EFacilityLookupCode.MODERATION;
            // Meeting room facilities (actual hotel rooms) are required if they're in the hotel's meeting room list
            const isMeetingRoomFacility = meetingRoomFacilityIds && Array.isArray(meetingRoomFacilityIds) && meetingRoomFacilityIds.includes(linkId);
            if (isAddonFacility || isMeetingRoomFacility) {
              return schema.of(componentsRequiredSchema).required();
            }
          } else if (!isPremium) {
            // For normal pricelists, require all classic components
            return schema.of(componentsRequiredSchema).required();
          }
        }
        // For non-base prices, require components only if price is set
        if (multiTax && price) {
          return schema.of(componentsRequiredSchema).required();
        } else {
          return schema.of(componentsOptionalSchema);
        }
        */
        return schema.of(componentsOptionalSchema);
      },
    ),
});

const validitySchema = yup.object().shape({
  validFrom: yup
    .date()
    .nullable()
    .test('validFrom-before-validTo', String(i18next.t('selector-validfrom-before-validto')), function (value) {
      const { validTo } = this.parent as { validTo?: Date | null };
      if (!value || !validTo) return true;
      return moment(value).isSameOrBefore(moment(validTo));
    }),
  validTo: yup
    .date()
    .nullable()
    .test('validTo-after-validFrom', String(i18next.t('selector-validto-after-validfrom')), function (value) {
      const { validFrom } = this.parent as { validFrom?: Date | null };
      if (!value || !validFrom) return true;
      return moment(value).isSameOrAfter(moment(validFrom));
    }),
});

const validationSchema = yup.object().shape({
  name: yup.string().required(() => String(i18next.t('selector-name-required'))).label(i18next.t('selector-name')),
  isBasePrice: yup.boolean().required(),
  minGuestCount: yup.number().nullable(),
  maxGuestCount: yup.number().nullable(),
  minItemCount: yup.number().nullable(),
  maxItemCount: yup.number().nullable(),
  publicCompanyId: yup.number().nullable(),
  includeMainRoomInPackage: yup.boolean().required(),
  validity: yup
    .array()
    .of(validitySchema)
    .test(
      'no-overlapping-validity-ranges',
      String(
        i18next.t('selector-validity-no-overlap'),
      ),
      function (value) {
        if (!value || value.length <= 1) return true;
        const ranges = value
          .filter(v => v && (v.validFrom !== null || v.validTo !== null))
          .map(v => ({
            start: v.validFrom ? moment(v.validFrom as any).startOf('day') : null,
            end: v.validTo ? moment(v.validTo as any).endOf('day') : null,
          }))
          .sort((a, b) => {
            if (a.start === null && b.start === null) return 0;
            if (a.start === null) return -1; // open from -infinity first
            if (b.start === null) return 1;
            return a.start.valueOf() - b.start.valueOf();
          });

        // fully open range (-∞, +∞) overlaps with anything
        if (ranges.some(r => r.start === null && r.end === null)) {
          return this.createError({ path: 'validity' });
        }

        for (let i = 1; i < ranges.length; i++) {
          const prev = ranges[i - 1];
          const curr = ranges[i];
          // Overlap if previous has no end (open-ended) or current.start <= previous.end
          if (prev.end === null || curr.start === null || curr.start.isSameOrBefore(prev.end)) {
            return this.createError({ path: 'validity' });
          }
        }
        return true;
      },
    ),
  weekdays: yup
    .array()
    .nullable()
    .of(
      yup
        .mixed<EPricingWeekDays>()
        .required()
        .test(value => Object.values(EPricingWeekDays).includes(value as EPricingWeekDays)),
    ),
  productsPrices: yup.array().of(productsSchema).required(),
  bundlesPrices: yup.array().of(bundlesSchema).required(),
  facilitiesPrices: yup.array().of(facilitiesSchema).required(),
});

type PriceSelectorFormType = yup.InferType<typeof validationSchema>;

interface MultiTaxFormProps {
  taxTypes: TaxTypeListOutput[];
  country: string;
  collection: CollectionType;
  index: number;
  currency: string;
  disabled?: boolean;
  onPriceComponentChanged?: (collection: CollectionType, index: number, cIndex: number) => void;
}

interface MultiTaxItemFormProps extends MultiTaxFormProps {
  cIndex: number;
}

function MultiTaxItemForm(props: MultiTaxItemFormProps) {
  const { control, watch, getValues } = useFormContext<PriceSelectorFormType>();

  const priceComponentWatch = watch(`${props.collection}.${props.index}.components.${props.cIndex}.price`);
  useEffect(() => {
    if (props.onPriceComponentChanged) {
      props.onPriceComponentChanged(props.collection, props.index, props.cIndex);
    }
  }, [priceComponentWatch]);

  return (
    <FormInputCurrency
      name={`${props.collection}.${props.index}.components.${props.cIndex}.price`}
      control={control}
      label={formatTaxTypeName(props.taxTypes, getValues(`${props.collection}.${props.index}.components.${props.cIndex}.taxTypeId`), props.country)}
      currency={props.currency}
      disabled={props.disabled}
    />
  );
}

function MultiTaxForm(props: MultiTaxFormProps) {
  const { control } = useFormContext<PriceSelectorFormType>();

  const { fields: componentsFields } = useFieldArray({
    control: control,
    name: `${props.collection}.${props.index}.components`,
  });

  return (
    <Grid container spacing={3}>
      {componentsFields.map((field, cIndex) => (
        <Grid item key={cIndex}>
          <MultiTaxItemForm {...props} cIndex={cIndex} />
        </Grid>
      ))}
    </Grid>
  );
}

interface SingleTaxFormProps {
  collection: CollectionType;
  index: number;
  currency: string;
  taxTypes: TaxTypeListOutput[];
  country: string;
  disabled?: boolean;
  onPriceChanged?: (collection: CollectionType, index: number) => void;
}

function SingleTaxForm(props: SingleTaxFormProps) {
  const { control, watch, getValues } = useFormContext<PriceSelectorFormType>();

  const { fields: componentsFields } = useFieldArray({
    control: control,
    name: `${props.collection}.${props.index}.components`,
  });

  const priceWatch = watch(`${props.collection}.${props.index}.price`);
  useEffect(() => {
    if (props.onPriceChanged) {
      props.onPriceChanged(props.collection, props.index);
    }
  }, [priceWatch]);

  return (
    <Grid container spacing={3}>
      {componentsFields.map((field, cIndex) => (
        <Grid item key={cIndex}>
          <FormInputCurrency
            name={`${props.collection}.${props.index}.price`}
            control={control}
            label={formatTaxTypeName(props.taxTypes, getValues(`${props.collection}.${props.index}.components.${cIndex}.taxTypeId`), props.country)}
            currency={props.currency}
            disabled={props.disabled}
          />
        </Grid>
      ))}
    </Grid>
  );
}

interface BundleModeListenerFormProps {
  index: number;
  onModeChanged: (index: number) => void;
}

function BundleModeListenerForm(props: BundleModeListenerFormProps) {
  const { control, watch, getValues } = useFormContext<PriceSelectorFormType>();

  const modeWatch = watch(`bundlesPrices.${props.index}.mode`);
  useEffect(() => {
    props.onModeChanged(props.index);
  }, [modeWatch]);

  return null;
}

function PriceSelectorForm(props: PriceSelectorFormProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const bookingMode = bookingModeSelector();

  const [updateMutateFunction] = useMutation(UPDATE_PRICESELECTOR_MUTATION);
  const [createMutateFunction] = useMutation(CREATE_PRICESELECTOR_MUTATION);

  const filter = filterSelector();
  const user = userSelector()!;
  const canEdit = props.data.id < 0 || canEditAdminRecord(user, props.data);

  const [updatedCycle, setUpdatedCycle] = useState(0);
  const [createdId, setCreatedId] = useState(0);

  // Track which combination products should be saved (for Epilo pricelists only)
  // We'll initialize these after the form is created
  const [vollpensionEzEnabled, setVollpensionEzEnabled] = useState(false);
  const [vollpensionDzEnabled, setVollpensionDzEnabled] = useState(false);
  const [switchesChanged, setSwitchesChanged] = useState(false);

  const showProductLinks = bookingMode === EBookingMode.SEM || user.isSeminargo || user.isRoot;

  useEffect(() => {
    if (createdId > 0) {
      if (props.inline && props.onNavigateToSelector) {
        props.onNavigateToSelector(createdId);
      } else {
        navigate(`/pricelists/pricelists/${props.data.priceList.id}/selectors/${createdId}`);
      }
    }
  }, [createdId, navigate, props.data.priceList.id, props.inline, props.onNavigateToSelector]);


  const _filterPrices = (p: any) => {
    if (!filter || !filter.spaceId) return true;
    if (p.price !== null && p.price !== undefined) return true;
    if (p.linkSpaceId === filter.spaceId) return true;
    return false;
  };

  const toFormSchema = (obj: PriceSelectorListOutput): PriceSelectorFormType => ({
    ...obj,
    publicCompanyId: (obj as any).publicCompanyId || null,
    includeMainRoomInPackage: obj.includeMainRoomInPackage ?? false,
    validity: obj.validity.length > 0 ? [...obj.validity.map(v => ({ validFrom: v.validFrom, validTo: v.validTo }))] : [{ validFrom: null, validTo: null }],
    weekdays: obj.weekdays ? [...obj.weekdays] : null,
    productsPrices: filterForSelectableRecords(user, props.products, props.data.space.id, false, true).flatMap(product => {
      const classicPrice = obj.prices.find(p => p.product && p.product.id === product.id && !p.isPremium);
      const premiumPrice = obj.prices.find(p => p.product && p.product.id === product.id && p.isPremium);

      const buildProductEntry = (
        variant: 'classic' | 'premium',
        priceRef: typeof classicPrice | typeof premiumPrice,
      ): PriceSelectorFormType['productsPrices'][number] => {
        const multiTax = product.components.length > 1;
        const components = product.components.map(c => {
          const componentPrice = priceRef ? priceRef.components.find(pc => pc.taxType.id === c.taxType.id) : null;
          return {
            taxTypeId: c.taxType.id,
            price: componentPrice ? componentPrice.price : null,
          };
        });
        const allComponentPricesPresent = components.every(c => c.price !== null && c.price !== undefined);
        const summedComponentsPrice = allComponentPricesPresent ? components.reduce((sum, c) => sum + (c.price || 0), 0) : null;
        const price = multiTax ? summedComponentsPrice : priceRef ? priceRef.price : null;
        return {
          linkId: product.id,
          linkSpace: product.space.name,
          linkSpaceId: product.space.id,
          name: product.name + (product.extRefCode ? ` (${product.extRefCode})` : ''),
          sku: product.sku,
          variant,
          lookupCode: product.lookupCode ?? null,
          isPremium: variant === 'premium',
          price,
          multiTax,
          singleTaxTypeId: multiTax || product.components.length === 0 ? null : product.components[0].taxType.id,
          components,
        };
      };

      const entries = [buildProductEntry('classic', classicPrice)];
      if (props.data.priceList.forLister || premiumPrice) {
        entries.push(buildProductEntry('premium', premiumPrice));
      }
      return entries;
    }),
    bundlesPrices: filterForSelectableRecords(user, props.bundles, props.data.space.id, false, true).flatMap(bundle => {
      const classicPrice = obj.prices.find(p => p.bundle && p.bundle.id === bundle.id && !p.isPremium);
      const premiumPrice = obj.prices.find(p => p.bundle && p.bundle.id === bundle.id && p.isPremium);

      const buildBundleEntry = (
        variant: 'classic' | 'premium',
        priceRef: typeof classicPrice | typeof premiumPrice,
      ): PriceSelectorFormType['bundlesPrices'][number] => {
        const mode = priceRef
          ? priceRef.bundlePriceFromProduct
            ? EBundleMode.PRODUCTS
            : EBundleMode.PRICE
          : classicPrice
            ? classicPrice.bundlePriceFromProduct
              ? EBundleMode.PRODUCTS
              : EBundleMode.PRICE
            : EBundleMode.NONE;

        // For Epilo pricelists (forLister=true), force PRICE mode for bundles that have pricing
        const finalMode = props.data.priceList.forLister ? EBundleMode.PRICE : mode;

        // Map bundle service types to hotel lister assignments
        const serviceTypeAssignments = (bundle.serviceTypes || []).map(bundleServiceType => {
          const serviceTypeSku = bundleServiceType.serviceType.sku;
          const hotelServiceType = props.data.priceList.forLister
            ? props.data.priceList.hotel?.serviceTypes?.find(hst => hst.serviceType.sku === serviceTypeSku && (hst as any).forLister)
            : props.data.priceList.hotel?.serviceTypes?.find(hst => hst.serviceType.sku === serviceTypeSku && (hst as any).forWidget);
          return {
            serviceTypeSku,
            listerAssignment: hotelServiceType?.listerServiceTypeAssignment || null,
          };
        });

        const components = bundle.components.map(c => {
          const bundleComponentPrice = priceRef
            ? priceRef.components.find(pc => pc.taxType.id === c.taxType.id)
            : null;
          return {
            taxTypeId: c.taxType.id,
            price: bundleComponentPrice ? bundleComponentPrice.price : null,
          };
        });
        const allComponentPricesPresent = components.every(c => c.price !== null && c.price !== undefined);
        const summedComponentsPrice = allComponentPricesPresent ? components.reduce((sum, c) => sum + (c.price || 0), 0) : null;
        const multiTax = finalMode === EBundleMode.PRODUCTS ? true : bundle.components.length > 1;
        const initialPrice = priceRef ? priceRef.price : null;
        const price = finalMode === EBundleMode.PRICE && multiTax ? summedComponentsPrice : initialPrice;

        return {
          linkId: bundle.id,
          linkSpace: bundle.space.name,
          linkSpaceId: bundle.space.id,
          name: bundle.name + (bundle.extRefCode ? ` (${bundle.extRefCode})` : ''),
          sku: bundle.sku,
          lookupCode: bundle.lookupCode ?? null,
          variant,
          isPremium: variant === 'premium',
          mode: finalMode,
          price,
          multiTax,
          singleTaxTypeId: multiTax || bundle.components.length === 0 ? null : bundle.components[0].taxType.id,
          serviceTypeAssignments,
          components,
        };
      };

      const entries = [buildBundleEntry('classic', classicPrice)];
      if (props.data.priceList.forLister || premiumPrice) {
        entries.push(buildBundleEntry('premium', premiumPrice));
      }

      return entries;
    }),
    facilitiesPrices: filterForSelectableRecords(user, props.facilities, props.data.space.id, false, true).flatMap(facility => {
      const classicPrice = obj.prices.find(p => p.facility && p.facility.id === facility.id && !p.isPremium);
      const premiumPrice = obj.prices.find(p => p.facility && p.facility.id === facility.id && p.isPremium);

      const buildFacilityEntry = (
        variant: 'classic' | 'premium',
        priceRef: typeof classicPrice | typeof premiumPrice,
      ): PriceSelectorFormType['facilitiesPrices'][number] => {
        const multiTax = facility.components.length > 1;
        const components = facility.components.map(c => {
          const pc = priceRef ? priceRef.components.find(pc => pc.taxType.id === c.taxType.id) : null;
          return {
            taxTypeId: c.taxType.id,
            price: pc ? pc.price : null,
          };
        });
        const allComponentPricesPresent = components.every(c => c.price !== null && c.price !== undefined);
        const summedComponentsPrice = allComponentPricesPresent ? components.reduce((sum, c) => sum + (c.price || 0), 0) : null;
        const price = multiTax ? summedComponentsPrice : priceRef ? priceRef.price : null;

        return {
          linkId: facility.id,
          linkSpace: facility.space.name,
          linkSpaceId: facility.space.id,
          name: facility.name + (facility.extRefCode ? ` (${facility.extRefCode})` : ''),
          sku: facility.sku,
          variant,
          lookupCode: facility.lookupCode ?? null,
          recurring: facility.recurring,
          isAddon: facility.isAddon,
          isPremium: variant === 'premium',
          price,
          multiTax,
          singleTaxTypeId: multiTax || facility.components.length === 0 ? null : facility.components[0].taxType.id,
          components,
        };
      };

      const entries = [buildFacilityEntry('classic', classicPrice)];
      if (props.data.priceList.forLister || premiumPrice) {
        entries.push(buildFacilityEntry('premium', premiumPrice));
      }
      return entries;
    }),
  });

  const apolloClient = useApolloClient();

  // Get meeting room facility IDs for validation
  const meetingRoomFacilityIds = new Set((props.data.priceList.hotel?.meetingRooms || []).map(mr => mr.facility?.id).filter(Boolean) as number[]);

  const form = useForm({
    mode: 'onChange',
    resolver: yupResolver(validationSchema) as any,
    context: {
      client: apolloClient,
      isEpiloPricelist: props.data.priceList.forLister,
      isBasePrice: props.data.isBasePrice,
      selectorSpaceId: props.data.space.id,
      meetingRoomFacilityIds: Array.from(meetingRoomFacilityIds),
      vollpensionEzEnabled: false,
      vollpensionDzEnabled: false,
    },
    defaultValues: toFormSchema((props.data || {}) as PriceSelectorListOutput),
  });
  const {
    handleSubmit,
    control,
    trigger,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors: validationErrors, isDirty, isValidating, isSubmitting },
  } = form;
  const isBasePrice = watch('isBasePrice');

  // Base prices must not be restricted by rule filters or renamed
  const canEditRules = canEdit && !isBasePrice;
  const canEditName = canEdit && !isBasePrice;

  const {
    fields: validityFields,
    append: validityAppend,
    remove: validityRemove,
  } = useFieldArray({
    control,
    name: 'validity',
  });
  const { fields: productsFields } = useFieldArray({
    control,
    name: 'productsPrices',
  });
  const { fields: bundlesFields } = useFieldArray({
    control,
    name: 'bundlesPrices',
  });
  const { fields: facilitiesFields } = useFieldArray({
    control,
    name: 'facilitiesPrices',
  });
  useEffect(() => {
    trigger();
  }, [trigger]);


  // Revalidate cross-dependent dates within the same validity row only
  useEffect(() => {
    const subscription = form.watch((_, { name }) => {
      if (!name) return;
      const match = name.match(/^validity\.(\d+)\.(validFrom|validTo)$/);
      if (!match) return;
      const index = match[1];
      const field = match[2];
      const counterpart = field === 'validFrom' ? 'validTo' : 'validFrom';
      trigger(`validity.${index}.${counterpart}` as any);
    });
    return () => subscription.unsubscribe();
  }, [form, trigger]);


  // Update validation context when switches change
  useEffect(() => {
    if (form.control._options?.context) {
      form.control._options.context.vollpensionEzEnabled = vollpensionEzEnabled;
      form.control._options.context.vollpensionDzEnabled = vollpensionDzEnabled;
      // Re-trigger validation when switches change
      trigger('productsPrices');
    }
  }, [vollpensionEzEnabled, vollpensionDzEnabled, form.control, trigger]);

  // Log missing prices for validation debugging
  useEffect(() => {
    if (validationErrors && Object.keys(validationErrors).length > 0) {
      const isEpiloPricelist = props.data.priceList.forLister;
      const selectorSpaceId = props.data.space.id;

      console.group('🔍 Validation Errors - Missing Prices');
      console.log('Pricelist type:', isEpiloPricelist ? 'seminargo (forLister)' : 'Normal');
      console.log('Is base price:', isBasePrice);
      console.log('Selector space ID:', selectorSpaceId);
      console.log('Vollpension EZ enabled:', vollpensionEzEnabled);
      console.log('Vollpension DZ enabled:', vollpensionDzEnabled);

      // Log product price errors
      if (validationErrors.productsPrices) {
        console.group('❌ Product Price Errors');
        const productsPrices = getValues('productsPrices');
        (validationErrors.productsPrices as any[])?.forEach((error, index) => {
          if (error) {
            const product = productsPrices[index];

            console.group(`Product ${index}: ${product?.name || 'Unknown'}`);
            console.log('Variant:', product?.variant);
            console.log('Lookup Code:', product?.lookupCode);
            console.log('Space ID:', product?.linkSpaceId, product?.linkSpaceId === selectorSpaceId ? '✓ Same space' : '⚠ Different space');
            console.log('Is Premium:', product?.isPremium);
            console.log('Multi-Tax:', product?.multiTax);
            console.log('Current Price:', product?.price);
            console.log('Error:', error);

            if (product?.lookupCode === EProductLookupCode.VOLLPENSIONSPAUSCHALE_EZ) {
              console.log('Vollpension EZ - Switch enabled:', vollpensionEzEnabled);
            }
            if (product?.lookupCode === EProductLookupCode.VOLLPENSIONSPAUSCHALE_DZ) {
              console.log('Vollpension DZ - Switch enabled:', vollpensionDzEnabled);
            }

            // Determine why validation might have failed
            const reasons = [];
            if (isBasePrice && product?.linkSpaceId === selectorSpaceId) {
              if (!product?.multiTax) {
                if (isEpiloPricelist) {
                  const isListerProduct = [
                    EProductLookupCode.ROOM_EZ,
                    EProductLookupCode.ROOM_DZ,
                    EProductLookupCode.ADDON_COFFEE,
                    EProductLookupCode.ADDON_COFFEEBREAK,
                    EProductLookupCode.ADDON_LUNCH,
                    EProductLookupCode.ADDON_DINNER
                  ].includes(product?.lookupCode as any);

                  const isCombinationWithSwitch =
                    (product?.lookupCode === EProductLookupCode.VOLLPENSIONSPAUSCHALE_EZ && vollpensionEzEnabled) ||
                    (product?.lookupCode === EProductLookupCode.VOLLPENSIONSPAUSCHALE_DZ && vollpensionDzEnabled);

                  if (isListerProduct || isCombinationWithSwitch) {
                    reasons.push('✓ Base price + Same space + seminargo lister product or combination with switch enabled → REQUIRED');
                  } else {
                    reasons.push('○ Base price + Same space + seminargo but not lister product → OPTIONAL');
                  }
                } else {
                  if (!product?.isPremium) {
                    const isCombination = product?.lookupCode === EProductLookupCode.VOLLPENSIONSPAUSCHALE_EZ ||
                      product?.lookupCode === EProductLookupCode.VOLLPENSIONSPAUSCHALE_DZ;
                    const isCombinationWithSwitch =
                      (product?.lookupCode === EProductLookupCode.VOLLPENSIONSPAUSCHALE_EZ && vollpensionEzEnabled) ||
                      (product?.lookupCode === EProductLookupCode.VOLLPENSIONSPAUSCHALE_DZ && vollpensionDzEnabled);

                    if (!isCombination || isCombinationWithSwitch) {
                      reasons.push('✓ Base price + Same space + Classic variant (not combination or switch enabled) → REQUIRED');
                    } else {
                      reasons.push('○ Base price + Same space + Classic variant + Combination without switch → OPTIONAL');
                    }
                  } else {
                    reasons.push('○ Base price + Same space + Premium variant (normal pricelist) → OPTIONAL');
                  }
                }
              } else {
                reasons.push('○ Multi-tax product (price calculated from components) → Price field not used');
              }
            } else if (!isBasePrice) {
              reasons.push('○ Not a base price → OPTIONAL');
            } else if (product?.linkSpaceId !== selectorSpaceId) {
              reasons.push('○ Different space → OPTIONAL');
            }

            console.log('Validation logic:', reasons.join('\n'));
            console.groupEnd();
          }
        });
        console.groupEnd();
      }

      // Log bundle price errors
      if (validationErrors.bundlesPrices) {
        console.group('❌ Bundle Price Errors');
        const bundlesPrices = getValues('bundlesPrices');
        (validationErrors.bundlesPrices as any[])?.forEach((error, index) => {
          if (error) {
            const bundle = bundlesPrices[index];

            console.group(`Bundle ${index}: ${bundle?.name || 'Unknown'}`);
            console.log('Variant:', bundle?.variant);
            console.log('Space ID:', bundle?.linkSpaceId, bundle?.linkSpaceId === selectorSpaceId ? '✓ Same space' : '⚠ Different space');
            console.log('Is Premium:', bundle?.isPremium);
            console.log('Mode:', bundle?.mode);
            console.log('Multi-Tax:', bundle?.multiTax);
            console.log('Current Price:', bundle?.price);
            console.log('Error:', error);

            const reasons = [];
            if (isBasePrice && bundle?.mode === EBundleMode.PRICE && bundle?.linkSpaceId === selectorSpaceId) {
              if (!bundle?.multiTax) {
                if (isEpiloPricelist || !bundle?.isPremium) {
                  reasons.push('✓ Base price + PRICE mode + Same space + (seminargo or Classic) → REQUIRED');
                } else {
                  reasons.push('○ Base price + PRICE mode + Same space + Premium (normal pricelist) → OPTIONAL');
                }
              } else {
                reasons.push('○ Multi-tax bundle (price calculated from components) → Price field not used');
              }
            } else if (bundle?.mode !== EBundleMode.PRICE) {
              reasons.push('○ Mode is not PRICE → OPTIONAL');
            } else if (!isBasePrice) {
              reasons.push('○ Not a base price → OPTIONAL');
            } else if (bundle?.linkSpaceId !== selectorSpaceId) {
              reasons.push('○ Different space → OPTIONAL');
            }

            console.log('Validation logic:', reasons.join('\n'));
            console.groupEnd();
          }
        });
        console.groupEnd();
      }

      // Log facility price errors
      if (validationErrors.facilitiesPrices) {
        console.group('❌ Facility Price Errors');
        const facilitiesPrices = getValues('facilitiesPrices');
        (validationErrors.facilitiesPrices as any[])?.forEach((error, index) => {
          if (error) {
            const facility = facilitiesPrices[index];

            console.group(`Facility ${index}: ${facility?.name || 'Unknown'}`);
            console.log('Variant:', facility?.variant);
            console.log('Lookup Code:', facility?.lookupCode);
            console.log('Space ID:', facility?.linkSpaceId, facility?.linkSpaceId === selectorSpaceId ? '✓ Same space' : '⚠ Different space');
            console.log('Is Premium:', facility?.isPremium);
            console.log('Is Addon:', facility?.isAddon);
            console.log('Multi-Tax:', facility?.multiTax);
            console.log('Current Price:', facility?.price);
            console.log('Error:', error);

            const reasons = [];
            if (isBasePrice && facility?.linkSpaceId === selectorSpaceId && !facility?.multiTax) {
              if (isEpiloPricelist) {
                const isAddonFacility = [
                  EFacilityLookupCode.BEAMER,
                  EFacilityLookupCode.FLIPCHART,
                  EFacilityLookupCode.PINNWAND,
                  EFacilityLookupCode.MODERATION
                ].includes(facility?.lookupCode as any);

                const meetingRoomIds = Array.from(meetingRoomFacilityIds);
                const isMeetingRoomFacility = meetingRoomIds.includes(facility?.linkId);

                if (isAddonFacility) {
                  reasons.push('✓ Base price + Same space + seminargo + Addon facility (equipment) → REQUIRED');
                } else if (isMeetingRoomFacility) {
                  reasons.push('✓ Base price + Same space + seminargo + Meeting room facility → REQUIRED');
                } else {
                  reasons.push('○ Base price + Same space + seminargo but not addon or meeting room → OPTIONAL');
                }
              } else {
                if (!facility?.isPremium) {
                  reasons.push('✓ Base price + Same space + Classic variant → REQUIRED');
                } else {
                  reasons.push('○ Base price + Same space + Premium variant (normal pricelist) → OPTIONAL');
                }
              }
            } else if (facility?.multiTax) {
              reasons.push('○ Multi-tax facility (price calculated from components) → Price field not used');
            } else if (!isBasePrice) {
              reasons.push('○ Not a base price → OPTIONAL');
            } else if (facility?.linkSpaceId !== selectorSpaceId) {
              reasons.push('○ Different space → OPTIONAL');
            }

            console.log('Validation logic:', reasons.join('\n'));
            console.groupEnd();
          }
        });
        console.groupEnd();
      }

      console.groupEnd();
    }
  }, [validationErrors, isBasePrice, vollpensionEzEnabled, vollpensionDzEnabled, props.data.priceList.forLister, props.data.space.id, getValues, props.products, meetingRoomFacilityIds]);

  // Initialize combination switches based on form values (after form is created)
  const [switchesInitialized, setSwitchesInitialized] = useState(false);
  useEffect(() => {
    if (!switchesInitialized && props.data.priceList.forLister && productsFields.length > 0) {
      const productsPrices = getValues('productsPrices');

      const hasEzPrice = productsPrices.some(
        p => p.lookupCode === EProductLookupCode.VOLLPENSIONSPAUSCHALE_EZ && p.price !== null && p.price !== undefined
      );
      const hasDzPrice = productsPrices.some(
        p => p.lookupCode === EProductLookupCode.VOLLPENSIONSPAUSCHALE_DZ && p.price !== null && p.price !== undefined
      );

      console.log('Initializing switches from form values:', {
        hasEzPrice,
        hasDzPrice,
        productsPricesCount: productsPrices.length,
        vollpensionProducts: productsPrices.filter(p =>
          p.lookupCode === EProductLookupCode.VOLLPENSIONSPAUSCHALE_EZ ||
          p.lookupCode === EProductLookupCode.VOLLPENSIONSPAUSCHALE_DZ
        )
      });

      setVollpensionEzEnabled(hasEzPrice);
      setVollpensionDzEnabled(hasDzPrice);
      setSwitchesInitialized(true);
    }
  }, [productsFields.length, switchesInitialized, props.data.priceList.forLister, getValues]);

  // Refresh form defaults when navigating between selectors
  useEffect(() => {
    if (isDirty) return;
    reset(toFormSchema((props.data || {}) as PriceSelectorListOutput));
    // Reset switches initialization flag when selector changes
    setSwitchesInitialized(false);
    setSwitchesChanged(false);
  }, [props.data?.id, props.baseSelector?.id ?? -1, props.products.length, props.bundles.length, props.facilities.length, isDirty, reset]);

  const onPriceComponentChanged = (collection: CollectionType, index: number, _cIndex: number) => {
    const collectionItem = getValues(`${collection}.${index}`);
    if (collectionItem && collectionItem.components) {
      if (collectionItem.components.find(c => c.price !== null && c.price !== undefined)) {
        const price = collectionItem.components.reduce((s, c) => s + c.price, 0);
        setValue(`${collection}.${index}.price`, price);
      } else {
        setValue(`${collection}.${index}.price`, null);
      }
    }
    if (collection === 'productsPrices') {
      onProductPriceChanged(index);
    }
  };
  const onProductPriceChanged = (_index: number) => {
    const bundlesPrices = getValues('bundlesPrices');
    const productsPrices = getValues('productsPrices');
    for (const [bundlePriceIndex, bundlePrice] of bundlesPrices.entries()) {
      const bundle = props.bundles.find(b => b.sku === bundlePrice.sku);

      if (!bundle || bundlePrice.mode !== EBundleMode.PRODUCTS) continue;

      let productPriceSum = 0;
      for (const productBundleItem of bundle.bundleItems) {
        const productPrice = productsPrices.find(p => p.sku === productBundleItem.product.sku && !p.isPremium);
        if (productPrice && productPrice.price !== null && productPrice.price !== undefined) {
          productPriceSum += productPrice.price * productBundleItem.includedCount;
        } else if (props.baseSelector && props.data.id !== props.baseSelector.id) {
          const baseProductPrice = props.baseSelector.prices.find(p => p.product && p.product.sku === productBundleItem.product.sku && !p.isPremium);
          if (baseProductPrice && baseProductPrice.price !== null && baseProductPrice.price !== undefined) {
            productPriceSum += baseProductPrice.price * productBundleItem.includedCount;
          }
        }
      }
      setValue(`bundlesPrices.${bundlePriceIndex}.price`, productPriceSum);
    }
  };

  const onSubmit = async (values: PriceSelectorFormType) => {
    try {
      if (props.data.id > 0) {
        const res = await updateMutateFunction({
          variables: {
            id: props.data.id,
            data: {
              name: values.name,
              minGuestCount: values.minGuestCount,
              maxGuestCount: values.maxGuestCount,
              minItemCount: values.minItemCount,
              maxItemCount: values.maxItemCount,
              validity: (values.validity || []).filter(v => v.validFrom !== null || v.validTo !== null),
              publicCompanyId: values.publicCompanyId || null,
              weekdays: values.weekdays,
              includeMainRoomInPackage: values.includeMainRoomInPackage,
              prices: [
                ...values.productsPrices
                  .filter(p => {
                    // Filter out combination products if their switches are disabled (Epilo pricelists only)
                    if (props.data.priceList.forLister) {
                      if (p.lookupCode === EProductLookupCode.VOLLPENSIONSPAUSCHALE_EZ && !vollpensionEzEnabled) {
                        return false;
                      }
                      if (p.lookupCode === EProductLookupCode.VOLLPENSIONSPAUSCHALE_DZ && !vollpensionDzEnabled) {
                        return false;
                      }
                    }
                    return p.price !== null && p.price !== undefined;
                  })
                  .map(p => ({
                    productId: p.linkId,
                    price: p.price,
                    isPremium: p.isPremium,
                    components:
                      p.multiTax &&
                      p.components?.map(c => ({
                        price: c.price || 0,
                        taxTypeId: c.taxTypeId,
                      })) ||
                      [],
                  })),
                ...values.facilitiesPrices
                  .filter(p => p.price !== null && p.price !== undefined)
                  .map(p => ({
                    facilityId: p.linkId,
                    price: p.price,
                    isPremium: p.isPremium,
                    components:
                      p.multiTax &&
                      p.components?.map(c => ({
                        price: c.price || 0,
                        taxTypeId: c.taxTypeId,
                      })) ||
                      [],
                  })),
                ...values.bundlesPrices
                  .filter(b => b.mode === EBundleMode.PRICE && b.price !== null && b.price !== undefined)
                  .map(b => ({
                    bundleId: b.linkId,
                    price: b.price,
                    bundlePriceFromProduct: false,
                    isPremium: b.isPremium,
                    components:
                      (b.multiTax && (b.price !== null && b.price !== undefined)) &&
                      b.components?.map(c => ({
                        price: c.price || 0,
                        taxTypeId: c.taxTypeId,
                      })) || [],
                  })),
                ...values.bundlesPrices
                  .filter(b => b.mode === EBundleMode.PRODUCTS)
                  .map(b => ({
                    bundleId: b.linkId,
                    price: null,
                    bundlePriceFromProduct: true,
                    isPremium: b.isPremium,
                    components: [],
                  })),
              ],
            },
          },
          update: cache => EVICT_PRICESELECTORS_QUERIES(cache),
          awaitRefetchQueries: true,
          refetchQueries: REFETCH_PRICESELECTORS_QUERIES(props.data.priceList.id, props.data.id),
        });
        reset(toFormSchema((res.data!.updatePriceSelector || {}) as PriceSelectorListOutput));
        setUpdatedCycle(updatedCycle + 1);
        setSwitchesChanged(false);
      } else {
        const res = await createMutateFunction({
          variables: {
            data: {
              priceListId: props.data.priceList.id,
              name: values.name,
              minGuestCount: values.minGuestCount,
              maxGuestCount: values.maxGuestCount,
              minItemCount: values.minItemCount,
              maxItemCount: values.maxItemCount,
              validity: (values.validity || []).filter(v => v.validFrom !== null || v.validTo !== null),
              publicCompanyId: values.publicCompanyId || null,
              weekdays: values.weekdays,
              includeMainRoomInPackage: values.includeMainRoomInPackage,
              prices: [
                ...values.productsPrices
                  .filter(p => {
                    // Filter out combination products if their switches are disabled (Epilo pricelists only)
                    if (props.data.priceList.forLister) {
                      if (p.lookupCode === EProductLookupCode.VOLLPENSIONSPAUSCHALE_EZ && !vollpensionEzEnabled) {
                        return false;
                      }
                      if (p.lookupCode === EProductLookupCode.VOLLPENSIONSPAUSCHALE_DZ && !vollpensionDzEnabled) {
                        return false;
                      }
                    }
                    return p.price !== null && p.price !== undefined;
                  })
                  .map(p => ({
                    productId: p.linkId,
                    price: p.price!,
                    isPremium: p.isPremium,
                    components:
                      (p.multiTax &&
                        p.components?.map(c => ({
                          price: c.price || 0,
                          taxTypeId: c.taxTypeId,
                        }))) ||
                      [],
                  })),
                ...values.facilitiesPrices
                  .filter(p => p.price !== null && p.price !== undefined)
                  .map(p => ({
                    facilityId: p.linkId,
                    price: p.price!,
                    isPremium: p.isPremium,
                    components:
                      (p.multiTax &&
                        p.components?.map(c => ({
                          price: c.price || 0,
                          taxTypeId: c.taxTypeId,
                        }))) ||
                      [],
                  })),
                ...values.bundlesPrices
                  .filter(b => b.mode === EBundleMode.PRICE && b.price !== null && b.price !== undefined)
                  .map(b => ({
                    bundleId: b.linkId,
                    price: b.price,
                    bundlePriceFromProduct: false,
                    isPremium: b.isPremium,
                    components:
                      (b.multiTax && b.price !== null && b.price !== undefined &&
                        b.components?.map(c => ({
                          price: c.price || 0,
                          taxTypeId: c.taxTypeId,
                        }))) ||
                      [],
                  })),
                ...values.bundlesPrices
                  .filter(b => b.mode === EBundleMode.PRODUCTS)
                  .map(b => ({
                    bundleId: b.linkId,
                    price: null,
                    bundlePriceFromProduct: true,
                    isPremium: b.isPremium,
                    components: [],
                  })),
              ],
            },
          },
          update: cache => EVICT_PRICESELECTORS_QUERIES(cache),
          awaitRefetchQueries: true,
          refetchQueries: REFETCH_PRICESELECTORS_QUERIES(props.data.priceList.id),
        });
        reset(toFormSchema((res.data!.createPriceSelector || {}) as PriceSelectorListOutput));
        setCreatedId(res.data!.createPriceSelector.id);
        setSwitchesChanged(false);
      }
      dispatchMessage(dispatch, i18next.t('selector-saved'));
    } catch (err) {
      dispatchException(dispatch, err);
    }
  };

  const initialPricesTabSelected = location.hash ? (location.hash.startsWith('#PRF') ? 2 : location.hash.startsWith('#PRB') ? 1 : 0) : 0;

  const getLocalizedProductName = (field: { name?: string | null; lookupCode?: EProductLookupCode | null }) => {
    const lookupToKey: Partial<Record<EProductLookupCode, string>> = {
      [EProductLookupCode.ROOM_EZ]: 'selector-lister-room-single',
      [EProductLookupCode.ROOM_DZ]: 'selector-lister-room-double',
      [EProductLookupCode.VOLLPENSIONSPAUSCHALE_EZ]: 'selector-lister-combinations-vollpension-ez',
      [EProductLookupCode.VOLLPENSIONSPAUSCHALE_DZ]: 'selector-lister-combinations-vollpension-dz',
      [EProductLookupCode.ADDON_COFFEE]: 'selector-lister-board-welcome-coffee',
      [EProductLookupCode.ADDON_COFFEEBREAK]: 'selector-lister-board-coffee-break',
      [EProductLookupCode.ADDON_LUNCH]: 'selector-lister-board-lunch',
      [EProductLookupCode.ADDON_DINNER]: 'selector-lister-board-dinner',
    };
    const key = field.lookupCode ? lookupToKey[field.lookupCode] : undefined;
    return key ? String(i18next.t(key, { defaultValue: field.name || '' })) : field.name || '';
  };

  const getLocalizedBundleName = (field: {
    name?: string | null;
    serviceTypeAssignments?: Array<{ listerAssignment?: string | null }> | null;
  }) => {
    const assignment = (field.serviceTypeAssignments || []).find(a => !!a?.listerAssignment)?.listerAssignment;
    const assignmentToKey: Record<string, string> = {
      SMALL: 'selector-lister-board-small',
      MEDIUM: 'selector-lister-board-medium',
      REGULAR: 'selector-lister-board-regular',
    };
    const key = assignment ? assignmentToKey[assignment] : undefined;
    return key ? String(i18next.t(key, { defaultValue: field.name || '' })) : field.name || '';
  };

  const getLocalizedFacilityName = (field: { name?: string | null; lookupCode?: EFacilityLookupCode | null }) => {
    const lookupToKey: Partial<Record<EFacilityLookupCode, string>> = {
      [EFacilityLookupCode.BEAMER]: 'selector-lister-beamer',
      [EFacilityLookupCode.FLIPCHART]: 'selector-lister-flipchart',
      [EFacilityLookupCode.PINNWAND]: 'selector-lister-pinnwand',
      [EFacilityLookupCode.MODERATION]: 'selector-lister-moderation',
    };
    const key = field.lookupCode ? lookupToKey[field.lookupCode] : undefined;
    return key ? String(i18next.t(key, { defaultValue: field.name || '' })) : field.name || '';
  };

  const defaultProductsTabContent = (
    <SimpleTable
      filter={productsFields.map((field, index) => _filterPrices(field))}
      rows={productsFields.map((field, index) => {
        const skuFieldPath = `productsPrices.${index}.sku` as const;
        const variantLabelSuffix =
          field.variant === 'premium' ? ` (${i18next.t('selector-lister-premium-label-short')})` : '';

        return [
          showProductLinks ? (
            <Link to={`/products/products/${field.linkId}`}>
              {getLocalizedProductName(field)}
              {variantLabelSuffix}
              <ScrollToHashElement id={`${watch(skuFieldPath)}${field.variant === 'premium' ? '-premium' : ''}`} />
            </Link>) : (<>{getLocalizedProductName(field)} {variantLabelSuffix}</>),
          ...(user.isSingleSpace ? [] : [field.linkSpace]),
          <FormInputCurrency
            key={`${field.id}.price`}
            name={`productsPrices.${index}.price`}
            control={control}
            label={i18next.t('selector-product-price')}
            currency={props.data.priceList.currency}
            disabled
          />,
          <>
            {watch(`productsPrices.${index}.multiTax`) && (
              <MultiTaxForm
                key={`${field.id}.productsPrices`}
                collection="productsPrices"
                index={index}
                onPriceComponentChanged={onPriceComponentChanged}
                taxTypes={props.taxTypes}
                country={props.data.priceList.space.country}
                currency={props.data.priceList.currency}
                disabled={!canEdit}
              />
            )}
            {!watch(`productsPrices.${index}.multiTax`) && (
              <SingleTaxForm
                key={`${field.id}.productsPrices`}
                collection="productsPrices"
                index={index}
                taxTypes={props.taxTypes}
                country={props.data.priceList.space.country}
                currency={props.data.priceList.currency}
                onPriceChanged={(_collection, _idx) => onProductPriceChanged(_idx)}
                disabled={!canEdit}
              />
            )}
          </>,
        ];
      })}
    />
  );

  const productsFieldEntries = productsFields.map((field, index) => ({ field, index }));

  const resolveRoomVariant = (lookup: EProductLookupCode, isPremium: boolean) =>
    productsFieldEntries.find(entry => entry.field.lookupCode === lookup && !!entry.field.isPremium === isPremium);

  const renderUnifiedRoomPriceCard = (
    productName: string,
    productIcon: React.ReactElement,
    classicEntry: { field: any; index: number } | undefined,
    premiumEntry: { field: any; index: number } | undefined,
    linkId: number,
    opts?: { isCombination?: boolean; lookupCode?: EProductLookupCode; subtitle?: string },
  ): React.ReactNode => {
    if (!classicEntry && !premiumEntry) {
      return (
        <Grid item xs={12} key={`${productName}-missing`}>
          <Alert severity="info">{i18next.t('selector-lister-room-missing')}</Alert>
        </Grid>
      );
    }

    const primaryEntry = classicEntry || premiumEntry!;

    // Determine if this is a combination product and which switch to use
    const isCombination = opts?.isCombination ?? false;
    const isVollpensionEz = opts?.lookupCode === EProductLookupCode.VOLLPENSIONSPAUSCHALE_EZ;
    const isVollpensionDz = opts?.lookupCode === EProductLookupCode.VOLLPENSIONSPAUSCHALE_DZ;
    const switchEnabled = isVollpensionEz ? vollpensionEzEnabled : isVollpensionDz ? vollpensionDzEnabled : true;

    const handleSwitchChange = (checked: boolean) => {
      // Update the switch state
      if (isVollpensionEz) {
        setVollpensionEzEnabled(checked);
      } else if (isVollpensionDz) {
        setVollpensionDzEnabled(checked);
      }

      // Mark switches as changed so the save button becomes enabled
      setSwitchesChanged(true);
    };

    const calculateTotalPrices = (index: number, multiTax: boolean) => {
      const components = watch(`productsPrices.${index}.components`);
      const basePrice = watch(`productsPrices.${index}.price`) || 0;
      const country = props.data.priceList.space.country;

      const getTaxRate = (taxType: TaxTypeListOutput) => {
        const taxRate = taxType.rates.find(r => r.country === country);
        if (taxRate) return taxRate.rate;
        const taxRateNoCountry = taxType.rates.find(r => !r.country);
        if (taxRateNoCountry) return taxRateNoCountry.rate;
        return 0;
      };

      if (multiTax && components && components.length > 0) {
        let netTotal = 0;
        let grossTotal = 0;

        components.forEach((comp: any) => {
          const taxType = props.taxTypes.find(t => t.id === comp.taxTypeId);
          const compPrice = comp.price || 0;

          if (props.data.priceList.isPricesNet) {
            // Preis ist netto, berechne brutto
            netTotal += compPrice;
            if (taxType) {
              const rate = getTaxRate(taxType);
              grossTotal += compPrice * (1 + rate);
            } else {
              grossTotal += compPrice;
            }
          } else {
            // Preis ist brutto, berechne netto
            grossTotal += compPrice;
            if (taxType) {
              const rate = getTaxRate(taxType);
              netTotal += compPrice / (1 + rate);
            } else {
              netTotal += compPrice;
            }
          }
        });

        return { net: netTotal, gross: grossTotal };
      } else {
        // Single tax
        const singleTaxTypeId = watch(`productsPrices.${index}.singleTaxTypeId`);
        const taxType = props.taxTypes.find(t => t.id === singleTaxTypeId);

        if (props.data.priceList.isPricesNet) {
          const net = basePrice;
          const gross = taxType ? basePrice * (1 + getTaxRate(taxType)) : basePrice;
          return { net, gross };
        } else {
          const gross = basePrice;
          const net = taxType ? basePrice / (1 + getTaxRate(taxType)) : basePrice;
          return { net, gross };
        }
      }
    };

    const renderPriceSection = (entry: { field: any; index: number } | undefined, variant: 'classic' | 'premium') => {
      if (!entry) {
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, opacity: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {variant === 'premium' ? i18next.t('selector-price-label-premium') : i18next.t('selector-price-label-classic-bar')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {i18next.t('selector-lister-price-not-configured')}
            </Typography>
          </Box>
        );
      }

      const { field, index } = entry;
      const multiTax = watch(`productsPrices.${index}.multiTax`);
      const prices = calculateTotalPrices(index, multiTax);

      // Disable price inputs if this is a combination product with switch disabled
      const priceInputDisabled = !canEdit || (isCombination && !switchEnabled);

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            {variant === 'premium' && <WorkspacePremiumIcon sx={{ color: 'warning.main', fontSize: 18 }} />}
            <Typography variant="body2" color={variant === 'premium' ? 'warning.main' : 'text.secondary'}>
              {variant === 'premium' ? i18next.t('selector-price-label-premium') : i18next.t('selector-price-label-classic-bar')}
            </Typography>
          </Box>
          {multiTax ? (
            <>
              <MultiTaxForm
                key={`${field.id}.productsPrices.card`}
                collection="productsPrices"
                index={index}
                onPriceComponentChanged={onPriceComponentChanged}
                taxTypes={props.taxTypes}
                country={props.data.priceList.space.country}
                currency={props.data.priceList.currency}
                disabled={priceInputDisabled}
              />
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {i18next.t('selector-price-total')}:
                </Typography>
                {prices.net > 0 && <>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {formatPrice(prices.net, i18next.language || 'de-AT', props.data.priceList.currency)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {i18next.t('selector-price-net')}
                  </Typography>
                  <Box sx={{ width: 16 }} />
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {formatPrice(prices.gross, i18next.language || 'de-AT', props.data.priceList.currency)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {i18next.t('selector-price-gross')}
                  </Typography>
                </>}
                {prices.net === 0 && <>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {i18next.t('selector-price-on-request')}
                  </Typography>
                </>}
              </Box>
            </>
          ) : (
            <>
              <SingleTaxForm
                key={`${field.id}.productsPrices.card`}
                collection="productsPrices"
                index={index}
                taxTypes={props.taxTypes}
                country={props.data.priceList.space.country}
                currency={props.data.priceList.currency}
                onPriceChanged={(_collection, _idx) => onProductPriceChanged(_idx)}
                disabled={priceInputDisabled}
              />
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {i18next.t('selector-price-total')}:
                </Typography>
                {prices.net > 0 && <>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {formatPrice(prices.net, i18next.language || 'de-AT', props.data.priceList.currency)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {i18next.t('selector-price-net')}
                  </Typography>
                  <Box sx={{ width: 16 }} />
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {formatPrice(prices.gross, i18next.language || 'de-AT', props.data.priceList.currency)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {i18next.t('selector-price-gross')}
                  </Typography>
                </>}
                {prices.net === 0 && <>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {i18next.t('selector-price-on-request')}
                  </Typography>
                </>}
              </Box>
            </>
          )}
        </Box>
      );
    };

    return (
      <Grid item xs={12} key={`${primaryEntry.field.id}-unified`}>
        <Box
          sx={{
            border: theme => `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {React.cloneElement(productIcon, { sx: { color: 'primary.main', fontSize: 24 } })}
                <Typography variant="h6">{productName}</Typography>
              </Box>
              {opts?.subtitle && (
                <Typography variant="caption" sx={{ ml: 4 }}>
                  {opts.subtitle}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {isCombination && props.data.priceList.forLister && (
                <Switch
                  checked={switchEnabled}
                  onChange={e => handleSwitchChange(e.target.checked)}
                  disabled={!canEdit}
                  color="primary"
                />
              )}
              {showProductLinks && (
                <Link to={`/products/products/${linkId}`}>{i18next.t('selector-lister-open-product')}</Link>
              )}
            </Box>
          </Box>
          {(user.isSeminargo || user.isRoot) && (
            <Typography variant="body2" color="text.secondary">
              {getLocalizedProductName(primaryEntry.field)}
            </Typography>
          )}
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'grey.300',
              borderRadius: 2,
              mt: 2,
              overflow: 'hidden',
            }}
          >
            <Grid container spacing={0}>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                  }}
                >
                  {renderPriceSection(classicEntry, 'classic')}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: 'warning.50',
                    borderLeft: '1px solid',
                    borderLeftColor: 'grey.300',
                  }}
                >
                  {renderPriceSection(premiumEntry, 'premium')}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Grid>
    );
  };

  const listerRoomSections: Array<{ code: EProductLookupCode; title: string; icon: React.ReactElement }> = [
    {
      code: EProductLookupCode.ROOM_EZ,
      title: i18next.t('selector-lister-room-single'),
      icon: <SingleBedIcon />,
    },
    {
      code: EProductLookupCode.ROOM_DZ,
      title: i18next.t('selector-lister-room-double'),
      icon: <KingBedIcon />,
    },
  ];

  const listerCombinationSections: Array<{ code: EProductLookupCode; title: string; subtitle: string; icon: React.ReactElement }> = [
    {
      code: EProductLookupCode.VOLLPENSIONSPAUSCHALE_EZ,
      title: i18next.t('selector-lister-combinations-vollpension-ez'),
      subtitle: i18next.t('selector-lister-combinations-vollpension-ez-subtitle'),
      icon: <RestaurantIcon />,
    },
    {
      code: EProductLookupCode.VOLLPENSIONSPAUSCHALE_DZ,
      title: i18next.t('selector-lister-combinations-vollpension-dz'),
      subtitle: i18next.t('selector-lister-combinations-vollpension-dz-subtitle'),
      icon: <RestaurantIcon />,
    },
  ];

  const listerRoomsTabContent = (() => {
    if (!props.data.priceList.forLister) return defaultProductsTabContent;

    const sections = listerRoomSections
      .map(section => {
        const classic = resolveRoomVariant(section.code, false);
        const premium = resolveRoomVariant(section.code, true);
        if (!classic && !premium) return null;
        return (
          <Box key={section.code} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Grid container spacing={3}>
              {renderUnifiedRoomPriceCard(section.title, section.icon, classic, premium, classic?.field.linkId || premium?.field.linkId || 0)}
            </Grid>
          </Box>
        );
      })
      .filter(Boolean) as React.ReactNode[];

    if (sections.length === 0) {
      return defaultProductsTabContent;
    }

    return (
      <Box
        sx={{
          border: theme => `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        {sections}
      </Box>
    );
  })();

  const listerCombinationsTabContent = (() => {
    if (!props.data.priceList.forLister) return null;

    const sections = listerCombinationSections
      .map(section => {
        const classic = resolveRoomVariant(section.code, false);
        const premium = resolveRoomVariant(section.code, true);
        if (!classic && !premium) return null;
        return (
          <Box key={section.code} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Grid container spacing={3}>
              {renderUnifiedRoomPriceCard(section.title, section.icon, classic, premium, classic?.field.linkId || premium?.field.linkId || 0, {
                isCombination: true,
                lookupCode: section.code,
                subtitle: section.subtitle,
              })}
            </Grid>
          </Box>
        );
      })
      .filter(Boolean) as React.ReactNode[];

    if (sections.length === 0) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {i18next.t('selector-lister-combinations-no-products')}
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          border: theme => `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <Alert severity="info">
          <Typography variant="body2">
            {i18next.t('selector-lister-combinations-description')}
          </Typography>
        </Alert>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>{sections}</Box>
      </Box>
    );
  })();

  const productsTabContent = props.data.priceList.forLister ? listerRoomsTabContent : defaultProductsTabContent;

  const bundlesFieldEntries = bundlesFields.map((field, index) => ({ field, index }));

  const renderBundlesTable = (entries: { field: (typeof bundlesFields)[number]; index: number }[] = bundlesFieldEntries) => {
    const keySuffix = entries === bundlesFieldEntries ? 'all' : entries.map(e => e.index).join('-');
    return (
      <SimpleTable
        key={`${updatedCycle}.bundles.${keySuffix}`}
        filter={entries.map(entry => _filterPrices(entry.field))}
        rows={entries.map(({ field, index }) => {
          const modeValue = watch(`bundlesPrices.${index}.mode`);
          const variantSuffix =
            field.variant === 'premium' ? ` (${i18next.t('selector-lister-premium-label-short')})` : '';

          // Force PRICE display for Epilo pricelists
          const displayMode = props.data.priceList.forLister && modeValue !== EBundleMode.NONE ? EBundleMode.PRICE : modeValue;

          return [
            props.data.priceList.forLister ? null : (
              showProductLinks ? (<Link to={`/products/bundles/${field.linkId}`}>
                {getLocalizedBundleName(field)}
                {variantSuffix}
                <ScrollToHashElement id={watch(`bundlesPrices.${index}.sku`)} />
              </Link>
              ) : (<>{getLocalizedBundleName(field)} {variantSuffix}</>)),
            ...(user.isSingleSpace ? [] : [field.linkSpace]),
            !props.data.priceList.forLister ? (
              <FormInputRadio
                key={`${field.id}.mode`}
                name={`bundlesPrices.${index}.mode`}
                control={control}
                options={[
                  {
                    label: i18next.t('enums-EBundleMode-NONE'),
                    value: EBundleMode.NONE,
                  },
                  {
                    label: i18next.t('enums-EBundleMode-PRODUCTS'),
                    value: EBundleMode.PRODUCTS,
                  },
                  {
                    label: i18next.t('enums-EBundleMode-PRICE'),
                    value: EBundleMode.PRICE,
                  },
                ]}
                disabled={!canEdit}
              />
            ) : null,
            displayMode !== EBundleMode.NONE && (
              <>
                <BundleModeListenerForm index={index} onModeChanged={() => onProductPriceChanged(0)} />
              </>
            ),
            modeValue !== EBundleMode.NONE && (
              <>
                <FormInputCurrency
                  key={`${field.id}.price`}
                  name={`bundlesPrices.${index}.price`}
                  control={control}
                  label={i18next.t('selector-bundle-price')}
                  currency={props.data.priceList.currency}
                  disabled
                />
                <BundleModeListenerForm index={index} onModeChanged={() => onProductPriceChanged(0)} />
              </>
            ),
            <Box sx={{ width: 210 }}>
              {displayMode === EBundleMode.PRICE && watch(`bundlesPrices.${index}.multiTax`) && (
                <MultiTaxForm
                  key={`${field.id}.bundlesPrices`}
                  collection="bundlesPrices"
                  index={index}
                  onPriceComponentChanged={onPriceComponentChanged}
                  taxTypes={props.taxTypes}
                  country={props.data.priceList.space.country}
                  currency={props.data.priceList.currency}
                  disabled={!canEdit}
                />
              )}
              {displayMode === EBundleMode.PRICE && !watch(`bundlesPrices.${index}.multiTax`) && (
                <SingleTaxForm
                  key={`${field.id}.bundlesPrices`}
                  collection="bundlesPrices"
                  index={index}
                  taxTypes={props.taxTypes}
                  country={props.data.priceList.space.country}
                  currency={props.data.priceList.currency}
                  disabled={!canEdit}
                />
              )}
            </Box>,
          ];
        })}
      />
    );
  };

  const renderUnifiedBundlePriceCard = (
    bundleName: string,
    bundleIcon: React.ReactElement,
    classicEntry: { field: (typeof bundlesFields)[number]; index: number } | undefined,
    premiumEntry: { field: (typeof bundlesFields)[number]; index: number } | undefined,
    linkId: number,
    subtitle?: string,
  ): React.ReactNode => {
    if (!classicEntry && !premiumEntry) {
      return (
        <Grid item xs={12} key={`${bundleName}-missing`}>
          <Alert severity="info">{i18next.t('selector-lister-bundle-missing')}</Alert>
        </Grid>
      );
    }

    const primaryEntry = classicEntry || premiumEntry!;

    const calculateBundleTotalPrices = (index: number, multiTax: boolean) => {
      const components = watch(`bundlesPrices.${index}.components`);
      const basePrice = watch(`bundlesPrices.${index}.price`) || 0;
      const country = props.data.priceList.space.country;

      const getTaxRate = (taxType: TaxTypeListOutput) => {
        const taxRate = taxType.rates.find(r => r.country === country);
        if (taxRate) return taxRate.rate;
        const taxRateNoCountry = taxType.rates.find(r => !r.country);
        if (taxRateNoCountry) return taxRateNoCountry.rate;
        return 0;
      };

      if (multiTax && components && components.length > 0) {
        let netTotal = 0;
        let grossTotal = 0;

        components.forEach((comp: any) => {
          const taxType = props.taxTypes.find(t => t.id === comp.taxTypeId);
          const compPrice = comp.price || 0;

          if (props.data.priceList.isPricesNet) {
            netTotal += compPrice;
            if (taxType) {
              const rate = getTaxRate(taxType);
              grossTotal += compPrice * (1 + rate);
            } else {
              grossTotal += compPrice;
            }
          } else {
            grossTotal += compPrice;
            if (taxType) {
              const rate = getTaxRate(taxType);
              netTotal += compPrice / (1 + rate);
            } else {
              netTotal += compPrice;
            }
          }
        });

        return { net: netTotal, gross: grossTotal };
      } else {
        const singleTaxTypeId = watch(`bundlesPrices.${index}.singleTaxTypeId`);
        const taxType = props.taxTypes.find(t => t.id === singleTaxTypeId);

        if (props.data.priceList.isPricesNet) {
          const net = basePrice;
          const gross = taxType ? basePrice * (1 + getTaxRate(taxType)) : basePrice;
          return { net, gross };
        } else {
          const gross = basePrice;
          const net = taxType ? basePrice / (1 + getTaxRate(taxType)) : basePrice;
          return { net, gross };
        }
      }
    };

    const renderBundlePriceSection = (
      entry: { field: (typeof bundlesFields)[number]; index: number } | undefined,
      variant: 'classic' | 'premium',
    ) => {
      if (!entry) {
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, opacity: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {variant === 'premium' ? i18next.t('selector-price-label-premium') : i18next.t('selector-price-label-classic-bar')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {i18next.t('selector-lister-price-not-configured')}
            </Typography>
          </Box>
        );
      }

      const { field, index } = entry;
      const modeValue = watch(`bundlesPrices.${index}.mode`);
      const multiTax = watch(`bundlesPrices.${index}.multiTax`);

      // Force PRICE display for Epilo pricelists
      const displayMode = props.data.priceList.forLister && modeValue !== EBundleMode.NONE ? EBundleMode.PRICE : modeValue;
      const prices = calculateBundleTotalPrices(index, multiTax);

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            {variant === 'premium' && <WorkspacePremiumIcon sx={{ color: 'warning.main', fontSize: 18 }} />}
            <Typography variant="body2" color={variant === 'premium' ? 'warning.main' : 'text.secondary'}>
              {variant === 'premium' ? i18next.t('selector-price-label-premium') : i18next.t('selector-price-label-classic-bar')}
            </Typography>
          </Box>
          {!props.data.priceList.forLister && (
            <FormInputRadio
              name={`bundlesPrices.${index}.mode`}
              control={control}
              options={[
                {
                  label: i18next.t('enums-EBundleMode-NONE'),
                  value: EBundleMode.NONE,
                },
                {
                  label: i18next.t('enums-EBundleMode-PRODUCTS'),
                  value: EBundleMode.PRODUCTS,
                },
                {
                  label: i18next.t('enums-EBundleMode-PRICE'),
                  value: EBundleMode.PRICE,
                },
              ]}
              disabled={!canEdit}
            />
          )}
          <BundleModeListenerForm index={index} onModeChanged={() => onProductPriceChanged(0)} />
          {displayMode === EBundleMode.PRICE ? (
            <>
              {multiTax ? (
                <>
                  <MultiTaxForm
                    key={`${field.id}.bundlesPrices.card`}
                    collection="bundlesPrices"
                    index={index}
                    onPriceComponentChanged={onPriceComponentChanged}
                    taxTypes={props.taxTypes}
                    country={props.data.priceList.space.country}
                    currency={props.data.priceList.currency}
                    disabled={!canEdit}
                  />
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {i18next.t('selector-price-total')}:
                    </Typography>
                    {prices.net > 0 && <>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {formatPrice(prices.net, i18next.language || 'de-AT', props.data.priceList.currency)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {i18next.t('selector-price-net')}
                      </Typography>
                      <Box sx={{ width: 16 }} />
                      <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        {formatPrice(prices.gross, i18next.language || 'de-AT', props.data.priceList.currency)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {i18next.t('selector-price-gross')}
                      </Typography>
                    </>}
                    {prices.net === 0 && <>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {i18next.t('selector-price-on-request')}
                      </Typography>
                    </>}
                  </Box>
                </>
              ) : (
                <>
                  <SingleTaxForm
                    key={`${field.id}.bundlesPrices.card`}
                    collection="bundlesPrices"
                    index={index}
                    taxTypes={props.taxTypes}
                    country={props.data.priceList.space.country}
                    currency={props.data.priceList.currency}
                    disabled={!canEdit}
                  />
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {i18next.t('selector-price-total')}:
                    </Typography>
                    {prices.net > 0 && <>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {formatPrice(prices.net, i18next.language || 'de-AT', props.data.priceList.currency)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {i18next.t('selector-price-net')}
                      </Typography>
                      <Box sx={{ width: 16 }} />
                      <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        {formatPrice(prices.gross, i18next.language || 'de-AT', props.data.priceList.currency)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {i18next.t('selector-price-gross')}
                      </Typography>
                    </>}
                    {prices.net === 0 && <>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {i18next.t('selector-price-on-request')}
                      </Typography>
                    </>}
                  </Box>
                </>
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {i18next.t('selector-lister-bundle-price-from-products')}
            </Typography>
          )}
        </Box>
      );
    };

    return (
      <Grid item xs={12} key={`${primaryEntry.field.id}-unified`}>
        <Box
          sx={{
            border: theme => `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {React.cloneElement(bundleIcon, { sx: { color: 'primary.main', fontSize: 24 } })}
                <Typography variant="h6">{bundleName}</Typography>
              </Box>
              {subtitle && (
                <Typography variant="caption" sx={{ ml: 4 }}>
                  {subtitle}
                </Typography>
              )}
            </Box>
            {showProductLinks && (
              <Link to={`/products/bundles/${linkId}`}>{i18next.t('selector-lister-open-bundle')}</Link>
            )}
          </Box>
          {(user.isSeminargo || user.isRoot) && (
            <Typography variant="body2" color="text.secondary">
              {getLocalizedBundleName(primaryEntry.field)}
            </Typography>
          )}
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'grey.300',
              borderRadius: 2,
              mt: 2,
              overflow: 'hidden',
            }}
          >
            <Grid container spacing={0}>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                  }}
                >
                  {renderBundlePriceSection(classicEntry, 'classic')}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: 'warning.50',
                    borderLeft: '1px solid',
                    borderLeftColor: 'grey.300',
                  }}
                >
                  {renderBundlePriceSection(premiumEntry, 'premium')}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Grid>
    );
  };

  const defaultBundlesTabContent = renderBundlesTable();

  const listerBundlesTabContent = (() => {
    if (!props.data.priceList.forLister) return defaultBundlesTabContent;

    const matchedIndexes = new Set<number>();

    // Create deterministic sections based on service-type assignments
    const serviceTypeSections = [
      {
        key: 'package-small',
        title: i18next.t('selector-lister-board-small'),
        subtitle: i18next.t('selector-lister-board-small-subtitle'),
        listerAssignment: 'SMALL',
        icon: <LocalCafeIcon />,
      },
      {
        key: 'package-medium',
        title: i18next.t('selector-lister-board-medium'),
        subtitle: i18next.t('selector-lister-board-medium-subtitle'),
        listerAssignment: 'MEDIUM',
        icon: <RoomServiceIcon />,
      },
      {
        key: 'package-regular',
        title: i18next.t('selector-lister-board-regular'),
        subtitle: i18next.t('selector-lister-board-regular-subtitle'),
        listerAssignment: 'REGULAR',
        icon: <RestaurantIcon />,
      },
    ];

    // Build sections based on service-type assignments only
    const serviceTypeBasedSections = serviceTypeSections
      .map(section => {
        const entries = bundlesFieldEntries.filter(entry => {
          const serviceTypeAssignments = entry.field.serviceTypeAssignments || [];

          // Check if listerAssignment matches
          const matchesServiceType = serviceTypeAssignments.some(assignment => assignment.listerAssignment === section.listerAssignment);

          // Fallback: Match by bundle name if listerAssignment is null
          let matchesByName = false;
          if (!matchesServiceType) {
            const bundleName = (entry.field.name || '').toLowerCase();
            if (section.listerAssignment === 'SMALL' && (bundleName.includes('small') || bundleName.includes('klein'))) {
              matchesByName = true;
            } else if (section.listerAssignment === 'MEDIUM' && (bundleName.includes('medium') || bundleName.includes('halbtags'))) {
              matchesByName = true;
            } else if (section.listerAssignment === 'REGULAR' && (bundleName.includes('regular') || bundleName.includes('ganztags'))) {
              matchesByName = true;
            }
          }

          const matches = matchesServiceType || matchesByName;
          if (matches) {
            matchedIndexes.add(entry.index);
            return true;
          }
          return false;
        });

        if (entries.length === 0) return null;

        const variants = entries.reduce(
          (acc, entry) => {
            acc[entry.field.variant] = entry;
            return acc;
          },
          {} as Record<'classic' | 'premium', { field: (typeof bundlesFields)[number]; index: number } | undefined>,
        );

        return (
          <Box key={section.key} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Grid container spacing={3}>
              {renderUnifiedBundlePriceCard(
                section.title,
                section.icon,
                variants.classic,
                variants.premium,
                variants.classic?.field.linkId || variants.premium?.field.linkId || 0,
                section.subtitle,
              )}
            </Grid>
          </Box>
        );
      })
      .filter(Boolean) as React.ReactNode[];

    // Einzelleistungen aus Products
    const singleServiceSections = [
      {
        key: 'welcome-coffee',
        title: i18next.t('selector-lister-board-welcome-coffee'),
        lookupCode: EProductLookupCode.ADDON_COFFEE,
        icon: <CoffeeIcon />,
      },
      {
        key: 'coffee-break',
        title: i18next.t('selector-lister-board-coffee-break'),
        lookupCode: EProductLookupCode.ADDON_COFFEEBREAK,
        icon: <FreeBreakfastIcon />,
      },
      {
        key: 'lunch',
        title: i18next.t('selector-lister-board-lunch'),
        lookupCode: EProductLookupCode.ADDON_LUNCH,
        icon: <LunchDiningIcon />,
      },
      {
        key: 'dinner',
        title: i18next.t('selector-lister-board-dinner'),
        lookupCode: EProductLookupCode.ADDON_DINNER,
        icon: <DinnerDiningIcon />,
      },
    ];

    const productSections = singleServiceSections
      .map(section => {
        const entries = productsFieldEntries.filter(entry => entry.field.lookupCode === section.lookupCode);

        if (entries.length === 0) return null;

        const variants = entries.reduce(
          (acc, entry) => {
            acc[entry.field.variant] = entry;
            return acc;
          },
          {} as Record<'classic' | 'premium', { field: (typeof productsFields)[number]; index: number } | undefined>,
        );

        return (
          <Box key={section.key} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Grid container spacing={3}>
              {renderUnifiedRoomPriceCard(
                section.title,
                section.icon,
                variants.classic,
                variants.premium,
                variants.classic?.field.linkId || variants.premium?.field.linkId || 0,
              )}
            </Grid>
          </Box>
        );
      })
      .filter(Boolean) as React.ReactNode[];

    // If no sections were created, show default bundles table
    if (serviceTypeBasedSections.length === 0 && productSections.length === 0) {
      return defaultBundlesTabContent;
    }

    return (
      <Box
        sx={{
          border: theme => `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        {/* Hauptraum-Einstellung */}
        <Box sx={{ mb: 2, p: 2, backgroundColor: theme => theme.palette.action.hover, borderRadius: 1 }}>
          <FormInputCheckbox
            name="includeMainRoomInPackage"
            control={control}
            label={i18next.t('pricelist-includemainroominpackage')}
            disabled={!canEdit}
          />
        </Box>

        {/* Pauschalen (nur mit Service-Type-Assignment) */}
        {serviceTypeBasedSections}

        {/* Einzelleistungen aus Products */}
        {productSections.length > 0 && (
          <>
            {serviceTypeBasedSections.length > 0 && <Divider sx={{ my: 2 }} />}
            <Typography variant="h5">{i18next.t('selector-lister-single-services')}</Typography>
            {productSections}
          </>
        )}
      </Box>
    );
  })();

  const bundlesTabContent = props.data.priceList.forLister ? listerBundlesTabContent : defaultBundlesTabContent;

  const facilitiesFieldEntries = facilitiesFields.map((field, index) => ({ field, index }));

  const renderUnifiedFacilityPriceCard = (
    facilityName: string,
    facilityIcon: React.ReactElement,
    classicEntry: { field: (typeof facilitiesFields)[number]; index: number } | undefined,
    premiumEntry: { field: (typeof facilitiesFields)[number]; index: number } | undefined,
    linkId: number,
    opts?: { emphasizeAddon?: boolean },
  ): React.ReactNode => {
    if (!classicEntry && !premiumEntry) {
      return (
        <Grid item xs={12} key={`${facilityName}-missing`}>
          <Alert severity="info">{i18next.t('selector-lister-facility-missing')}</Alert>
        </Grid>
      );
    }

    const primaryEntry = classicEntry || premiumEntry!;

    const isRoomAddon =
      opts?.emphasizeAddon !== false &&
      (primaryEntry.field.lookupCode === EFacilityLookupCode.SEMINARROOM_FULLDAY ||
        primaryEntry.field.lookupCode === EFacilityLookupCode.SEMINARROOM_SEMIDAY);

    const calculateFacilityTotalPrices = (index: number, multiTax: boolean) => {
      const components = watch(`facilitiesPrices.${index}.components`);
      const basePrice = watch(`facilitiesPrices.${index}.price`) || 0;
      const country = props.data.priceList.space.country;

      const getTaxRate = (taxType: TaxTypeListOutput) => {
        const taxRate = taxType.rates.find(r => r.country === country);
        if (taxRate) return taxRate.rate;
        const taxRateNoCountry = taxType.rates.find(r => !r.country);
        if (taxRateNoCountry) return taxRateNoCountry.rate;
        return 0;
      };

      if (multiTax && components && components.length > 0) {
        let netTotal = 0;
        let grossTotal = 0;

        components.forEach((comp: any) => {
          const taxType = props.taxTypes.find(t => t.id === comp.taxTypeId);
          const compPrice = comp.price || 0;

          if (props.data.priceList.isPricesNet) {
            netTotal += compPrice;
            if (taxType) {
              const rate = getTaxRate(taxType);
              grossTotal += compPrice * (1 + rate);
            } else {
              grossTotal += compPrice;
            }
          } else {
            grossTotal += compPrice;
            if (taxType) {
              const rate = getTaxRate(taxType);
              netTotal += compPrice / (1 + rate);
            } else {
              netTotal += compPrice;
            }
          }
        });

        return { net: netTotal, gross: grossTotal };
      } else {
        const singleTaxTypeId = watch(`facilitiesPrices.${index}.singleTaxTypeId`);
        const taxType = props.taxTypes.find(t => t.id === singleTaxTypeId);

        if (props.data.priceList.isPricesNet) {
          const net = basePrice;
          const gross = taxType ? basePrice * (1 + getTaxRate(taxType)) : basePrice;
          return { net, gross };
        } else {
          const gross = basePrice;
          const net = taxType ? basePrice / (1 + getTaxRate(taxType)) : basePrice;
          return { net, gross };
        }
      }
    };

    const renderFacilityPriceSection = (
      entry: { field: (typeof facilitiesFields)[number]; index: number } | undefined,
      variant: 'classic' | 'premium',
    ) => {
      if (!entry) {
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, opacity: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {variant === 'premium' ? i18next.t('selector-price-label-premium') : i18next.t('selector-price-label-classic-bar')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {i18next.t('selector-lister-price-not-configured')}
            </Typography>
          </Box>
        );
      }

      const { field, index } = entry;
      const multiTax = watch(`facilitiesPrices.${index}.multiTax`);
      const prices = calculateFacilityTotalPrices(index, multiTax);

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            {variant === 'premium' && <WorkspacePremiumIcon sx={{ color: 'warning.main', fontSize: 18 }} />}
            <Typography variant="body2" color={variant === 'premium' ? 'warning.main' : 'text.secondary'}>
              {variant === 'premium' ? i18next.t('selector-price-label-premium') : i18next.t('selector-price-label-classic-bar')}
            </Typography>
          </Box>
          {multiTax ? (
            <>
              <MultiTaxForm
                key={`${field.id}.facilitiesPrices.card`}
                collection="facilitiesPrices"
                index={index}
                onPriceComponentChanged={onPriceComponentChanged}
                taxTypes={props.taxTypes}
                country={props.data.priceList.space.country}
                currency={props.data.priceList.currency}
                disabled={!canEdit}
              />
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {i18next.t('selector-price-total')}:
                </Typography>
                {prices.net > 0 && <>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {formatPrice(prices.net, i18next.language || 'de-AT', props.data.priceList.currency)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {i18next.t('selector-price-net')}
                  </Typography>
                  <Box sx={{ width: 16 }} />
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {formatPrice(prices.gross, i18next.language || 'de-AT', props.data.priceList.currency)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {i18next.t('selector-price-gross')}
                  </Typography>
                </>}
                {prices.net === 0 && <>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {i18next.t('selector-price-on-request')}
                  </Typography>
                </>}
              </Box>
            </>
          ) : (
            <>
              <SingleTaxForm
                key={`${field.id}.facilitiesPrices.card`}
                collection="facilitiesPrices"
                index={index}
                taxTypes={props.taxTypes}
                country={props.data.priceList.space.country}
                currency={props.data.priceList.currency}
                disabled={!canEdit}
              />
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {i18next.t('selector-price-total')}:
                </Typography>
                {prices.net > 0 && <>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {formatPrice(prices.net, i18next.language || 'de-AT', props.data.priceList.currency)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {i18next.t('selector-price-net')}
                  </Typography>
                  <Box sx={{ width: 16 }} />
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {formatPrice(prices.gross, i18next.language || 'de-AT', props.data.priceList.currency)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {i18next.t('selector-price-gross')}
                  </Typography>
                </>}
                {prices.net === 0 && <>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {i18next.t('selector-price-on-request')}
                  </Typography>
                </>}
              </Box>
            </>
          )}
        </Box>
      );
    };

    return (
      <Grid item xs={12} key={`${primaryEntry.field.id}-unified`}>
        <Box
          sx={{
            border: theme => `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            ...(isRoomAddon
              ? {
                borderColor: 'primary.main',
                borderWidth: 2,
                backgroundColor: 'primary.50',
              }
              : {}),
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {React.cloneElement(facilityIcon, { sx: { color: 'primary.main', fontSize: 24 } })}
              <Typography variant="h6" sx={isRoomAddon ? { fontWeight: 'bold' } : {}}>
                {facilityName}
              </Typography>
            </Box>
            {showProductLinks && (
              <Link to={`/products/facilities/${linkId}`}>{i18next.t('selector-lister-open-facility')}</Link>
            )}
          </Box>
          {(user.isSeminargo || user.isRoot) && (
            <Typography variant="body2" color="text.secondary">
              {getLocalizedFacilityName(primaryEntry.field)}
            </Typography>
          )}
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'grey.300',
              borderRadius: 2,
              mt: 2,
              overflow: 'hidden',
            }}
          >
            <Grid container spacing={0}>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                  }}
                >
                  {renderFacilityPriceSection(classicEntry, 'classic')}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: 'warning.50',
                    borderLeft: '1px solid',
                    borderLeftColor: 'grey.300',
                  }}
                >
                  {renderFacilityPriceSection(premiumEntry, 'premium')}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Grid>
    );
  };

  // Lister-relevant facilities (matches validation in seminargo-server-db/src/validation/lister.ts)
  const amenitySections: Array<{ code: EFacilityLookupCode; title: string; icon: React.ReactElement }> = [
    {
      code: EFacilityLookupCode.BEAMER,
      title: i18next.t('selector-lister-beamer'),
      icon: <VideocamIcon />,
    },
    {
      code: EFacilityLookupCode.FLIPCHART,
      title: i18next.t('selector-lister-flipchart'),
      icon: <DashboardIcon />,
    },
    {
      code: EFacilityLookupCode.PINNWAND,
      title: i18next.t('selector-lister-pinnwand'),
      icon: <PushPinIcon />,
    },
    {
      code: EFacilityLookupCode.MODERATION,
      title: i18next.t('selector-lister-moderation'),
      icon: <BusinessCenterIcon />,
    },
  ];

  const defaultFacilitiesTabContent = (
    <SimpleTable
      filter={facilitiesFields.map((field, _index) => _filterPrices(field))}
      rows={facilitiesFields.map((field, index) => [
        showProductLinks ? (<Link to={`/products/facilities/${field.linkId}`}>
          {getLocalizedFacilityName(field)}
          <ScrollToHashElement id={watch(`facilitiesPrices.${index}.sku`)} />
        </Link>) : (<>{getLocalizedFacilityName(field)} <ScrollToHashElement id={watch(`facilitiesPrices.${index}.sku`)} /></>),
        ...(user.isSingleSpace ? [] : [field.linkSpace]),
        <FormInputCurrency
          key={`${field.id}.price`}
          name={`facilitiesPrices.${index}.price`}
          control={control}
          label={i18next.t('selector-facility-price')}
          currency={props.data.priceList.currency}
          disabled
        />,
        <>
          {watch(`facilitiesPrices.${index}.multiTax`) && (
            <MultiTaxForm
              key={`${field.id}.facilitiesPrices`}
              collection="facilitiesPrices"
              index={index}
              onPriceComponentChanged={onPriceComponentChanged}
              taxTypes={props.taxTypes}
              country={props.data.priceList.space.country}
              currency={props.data.priceList.currency}
              disabled={!canEdit}
            />
          )}
          {!watch(`facilitiesPrices.${index}.multiTax`) && (
            <SingleTaxForm
              key={`${field.id}.facilitiesPrices`}
              collection="facilitiesPrices"
              index={index}
              taxTypes={props.taxTypes}
              country={props.data.priceList.space.country}
              currency={props.data.priceList.currency}
              disabled={!canEdit}
            />
          )}
        </>,
      ])}
    />
  );

  const listerAmenitiesTabContent = (() => {
    if (!props.data.priceList.forLister) return defaultFacilitiesTabContent;

    const matchedIndexes = new Set<number>();

    const sections = amenitySections
      .map(section => {
        const entries = facilitiesFieldEntries.filter(entry => entry.field.lookupCode === section.code);
        if (entries.length === 0) return null;

        entries.forEach(entry => matchedIndexes.add(entry.index));

        const variants = entries.reduce(
          (acc, entry) => {
            acc[entry.field.variant] = entry;
            return acc;
          },
          {} as Record<'classic' | 'premium', { field: (typeof facilitiesFields)[number]; index: number } | undefined>,
        );

        return (
          <Box key={section.code} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Grid container spacing={3}>
              {renderUnifiedFacilityPriceCard(
                section.title,
                section.icon,
                variants.classic,
                variants.premium,
                variants.classic?.field.linkId || variants.premium?.field.linkId || 0,
              )}
            </Grid>
          </Box>
        );
      })
      .filter(Boolean) as React.ReactNode[];

    // Meeting rooms section - show facilities linked to hotel meeting rooms
    const meetingRoomFacilityIds = new Set((props.data.priceList.hotel?.meetingRooms || []).map(mr => mr.facility?.id).filter(Boolean) as number[]);

    const meetingRoomSections: React.ReactNode[] = [];

    if (meetingRoomFacilityIds.size > 0) {
      const meetingRoomEntries = facilitiesFieldEntries.filter(entry => meetingRoomFacilityIds.has(entry.field.linkId));

      if (meetingRoomEntries.length > 0) {
        meetingRoomEntries.forEach(entry => matchedIndexes.add(entry.index));

        // Group by facility ID to get classic/premium variants
        const facilitiesById = meetingRoomEntries.reduce(
          (acc, entry) => {
            const facilityId = entry.field.linkId;
            if (!acc[facilityId]) {
              acc[facilityId] = { classic: undefined, premium: undefined, name: entry.field.name || 'Unbekannter Raum' };
            }
            acc[facilityId][entry.field.variant] = entry;
            return acc;
          },
          {} as Record<
            number,
            {
              classic?: { field: (typeof facilitiesFields)[number]; index: number };
              premium?: { field: (typeof facilitiesFields)[number]; index: number };
              name: string;
            }
          >,
        );

        Object.entries(facilitiesById).forEach(([facilityId, variants]) => {
          const meetingRoom = props.data.priceList.hotel?.meetingRooms?.find(mr => mr.facility?.id === parseInt(facilityId));
          const roomName = meetingRoom?.name || variants.name || `Raum ${facilityId}`;

          meetingRoomSections.push(
            <Box key={`meetingroom-${facilityId}`} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Grid container spacing={3}>
                {renderUnifiedFacilityPriceCard(roomName, <BusinessCenterIcon />, variants.classic, variants.premium, parseInt(facilityId), {
                  emphasizeAddon: false,
                })}
              </Grid>
            </Box>,
          );
        });
      }
    }

    // For lister pricelists, don't show any unmatched/additional facilities
    const unmatchedContent = null;

    if (sections.length === 0 && meetingRoomSections.length === 0 && !unmatchedContent) {
      return defaultFacilitiesTabContent;
    }

    return (
      <Box
        sx={{
          border: theme => `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        {/* Technik & Ausstattung */}
        {sections}

        {/* Seminarräume */}
        {meetingRoomSections.length > 0 && (
          <>
            {sections.length > 0 && <Divider sx={{ my: 2 }} />}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h5">{i18next.t('selector-lister-meetingrooms')}</Typography>

              {props.data.priceList.hotel && (
                <Link to={`/settings/hotels/${props.data.priceList.hotel.id}/epilo#capacities`} style={{ fontSize: '0.875rem' }}>
                  {i18next.t('selector-lister-edit-meetingrooms')}
                </Link>
              )}
            </Box>
            <Box sx={{ mb: 2, ml: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {i18next.t('selector-lister-meetingrooms-included')}
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                  {i18next.t('selector-lister-meetingrooms-equipment-beamer')}
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                  {i18next.t('selector-lister-meetingrooms-equipment-flipchart')}
                </Typography>
                <Typography component="li" variant="body2">
                  {i18next.t('selector-lister-meetingrooms-equipment-pinnwand')}
                </Typography>
              </Box>
            </Box>
            {meetingRoomSections}
          </>
        )}

        {/* Hinweis falls keine Seminarräume verknüpft */}
        {meetingRoomSections.length === 0 && (
          <>
            {sections.length > 0 && <Divider sx={{ my: 2 }} />}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h5">{i18next.t('selector-lister-meetingrooms')}</Typography>
              {props.data.priceList.hotel && (
                <Link to={`/settings/hotels/${props.data.priceList.hotel.id}/epilo#capacities`} style={{ fontSize: '0.875rem' }}>
                  {i18next.t('selector-lister-edit-meetingrooms')}
                </Link>
              )}
            </Box>
            <Box sx={{ mb: 2, ml: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {i18next.t('selector-lister-meetingrooms-included')}
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                  {i18next.t('selector-lister-meetingrooms-equipment-beamer')}
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                  {i18next.t('selector-lister-meetingrooms-equipment-flipchart')}
                </Typography>
                <Typography component="li" variant="body2">
                  {i18next.t('selector-lister-meetingrooms-equipment-pinnwand')}
                </Typography>
              </Box>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Alert severity="info">
                  {i18next.t('selector-lister-meetingrooms-missing')}
                </Alert>
              </Grid>
            </Grid>
          </>
        )}

        {unmatchedContent}
      </Box>
    );
  })();

  const facilitiesTabContent = props.data.priceList.forLister ? listerAmenitiesTabContent : defaultFacilitiesTabContent;

  // Watch productsPrices to trigger recalculation when prices change
  const watchedProductsPrices = watch('productsPrices');

  // Calculate hasError flags per tab for Epilo pricelists
  // Use a stringified version of validationErrors to ensure it triggers re-calculation
  const validationErrorsKey = JSON.stringify(validationErrors);

  const hasRoomErrors = useMemo(() => {
    if (!props.data.priceList.forLister || !validationErrors.productsPrices) {
      console.log('📊 hasRoomErrors: false (no forLister or no product errors)');
      return false;
    }

    const roomLookupCodes = [EProductLookupCode.ROOM_EZ, EProductLookupCode.ROOM_DZ];

    const hasErrors = (validationErrors.productsPrices as any[])?.some((error, index) => {
      if (!error) return false;
      const product = watchedProductsPrices[index];
      const matches = product && roomLookupCodes.includes(product.lookupCode as EProductLookupCode);
      if (matches) {
        console.log('🛏️ Room error found:', product.name, product.lookupCode, error);
      }
      return matches;
    });

    console.log('📊 hasRoomErrors:', hasErrors);
    return hasErrors;
  }, [validationErrorsKey, props.data.priceList.forLister, watchedProductsPrices, validationErrors.productsPrices]);

  const hasIndividualProductErrors = useMemo(() => {
    if (!props.data.priceList.forLister || !validationErrors.productsPrices) {
      console.log('📊 hasIndividualProductErrors: false (no forLister or no product errors)');
      return false;
    }

    const individualProductLookupCodes = [
      EProductLookupCode.ADDON_COFFEE,
      EProductLookupCode.ADDON_COFFEEBREAK,
      EProductLookupCode.ADDON_LUNCH,
      EProductLookupCode.ADDON_DINNER,
    ];

    const hasErrors = (validationErrors.productsPrices as any[])?.some((error, index) => {
      if (!error) return false;
      const product = watchedProductsPrices[index];
      const matches = product && individualProductLookupCodes.includes(product.lookupCode as EProductLookupCode);
      if (matches) {
        console.log('🍽️ Individual product error found:', product.name, product.lookupCode, error);
      }
      return matches;
    });

    console.log('📊 hasIndividualProductErrors:', hasErrors);
    return hasErrors;
  }, [validationErrorsKey, props.data.priceList.forLister, watchedProductsPrices, validationErrors.productsPrices]);

  const hasBoardErrors = useMemo(() => {
    if (!props.data.priceList.forLister) return Boolean(validationErrors.bundlesPrices);
    // For Epilo pricelists, board tab includes bundles AND individual products
    const bundleErrors = Boolean(validationErrors.bundlesPrices);
    const result = bundleErrors || hasIndividualProductErrors;
    console.log('📊 hasBoardErrors:', result, '(bundles:', bundleErrors, 'individual:', hasIndividualProductErrors, ')');
    return result;
  }, [validationErrorsKey, hasIndividualProductErrors, props.data.priceList.forLister, validationErrors.bundlesPrices]);

  const hasCombinationErrors = useMemo(() => {
    if (!props.data.priceList.forLister || !validationErrors.productsPrices) {
      console.log('📊 hasCombinationErrors: false (no forLister or no product errors)');
      return false;
    }

    const combinationLookupCodes = [EProductLookupCode.VOLLPENSIONSPAUSCHALE_EZ, EProductLookupCode.VOLLPENSIONSPAUSCHALE_DZ];

    const hasErrors = (validationErrors.productsPrices as any[])?.some((error, index) => {
      if (!error) return false;
      const product = watchedProductsPrices[index];
      const matches = product && combinationLookupCodes.includes(product.lookupCode as EProductLookupCode);
      if (matches) {
        console.log('🍴 Combination error found:', product.name, product.lookupCode, error);
      }
      return matches;
    });

    console.log('📊 hasCombinationErrors:', hasErrors);
    return hasErrors;
  }, [validationErrorsKey, props.data.priceList.forLister, watchedProductsPrices, validationErrors.productsPrices]);

  return (
    <>
      {!props.inline && (
        <Helmet>
          <title>
            {formatDocumentTitle([
              i18next.t('pricelist-selector-page-title'),
              props.data.priceList,
              {
                ...props.data,
                space: null,
                name: props.data.isBasePrice ? i18next.t('pricelist-selector-baseprice') : props.data.name,
              },
            ])}
          </title>
        </Helmet>
      )}
      <Grid container spacing={3}>
        <FormProvider {...form}>
          <UnsavedChangesPrompt isDirty={isDirty} />
          {!props.inline && (
            <Grid item xs={12}>
              <Link to={`/pricelists/pricelists/${props.data.priceList.id}`}>{i18next.t('selector-backlink')}</Link>
            </Grid>
          )}
          {isBasePrice && (
            <Grid item xs={12}>
              <Alert severity="info">
                {i18next.t('selector-baseprice-info')}
              </Alert>
            </Grid>
          )}
          {!isBasePrice && (
            <>
              <Grid item xs={12} sm={6}>
                <FormInputText name="name" control={control} label={i18next.t('selector-name')} required disabled={!canEditName} />
              </Grid>
              <Grid item xs={false} sm={6} />
              <Grid item xs={12} sm={3}>
                <FormInputNumber name="minGuestCount" control={control} label={i18next.t('selector-minguestcount')} disabled={!canEditRules} helperText={i18next.t('selector-minguestcount-helpertext')} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormInputNumber name="maxGuestCount" control={control} label={i18next.t('selector-maxguestcount')} disabled={!canEditRules} helperText={i18next.t('selector-maxguestcount-helpertext')} />
              </Grid>
              {/*
              <Grid item xs={12} sm={3}>
                <FormInputNumber name="minItemCount" control={control} label={i18next.t('selector-minitemcount')} disabled={!canEditRules} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormInputNumber name="maxItemCount" control={control} label={i18next.t('selector-maxitemcount')} disabled={!canEditRules} />
              </Grid>
              */}
              <Grid item xs={12} sm={6}></Grid>
              <Grid item xs={12} sm={6}><Grid container spacing={3}>
                {validityFields.map((field, index) => (
                  <React.Fragment key={field.id}>
                    <Grid item xs={12} sm={4}>
                      <FormInputDate name={`validity.${index}.validFrom`} control={control} label={i18next.t('selector-validfrom')} disabled={!canEditRules} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormInputDate name={`validity.${index}.validTo`} control={control} label={i18next.t('selector-validto')} disabled={!canEditRules} />
                    </Grid>
                    {canEdit && (
                      <Grid item xs={12} sm={4}>
                        <IconButton onClick={() => validityRemove(index)}>
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    )}
                  </React.Fragment>
                ))}
                {canEdit && (
                  <Grid item xs={12}>
                    <Button startIcon={<AddIcon />} onClick={() => validityAppend({ validFrom: null, validTo: null })}>
                      {i18next.t('pricelist-validity-add')}
                    </Button>
                  </Grid>
                )}
              </Grid></Grid>
              {props.data.priceList.forLister && (
                <Grid item xs={12} sm={3}>
                  <FormInputDropdown
                    name="publicCompanyId"
                    control={control}
                    label={i18next.t('selector-customer')}
                    options={[
                      { value: null, label: i18next.t('selector-customer-all') },
                      ...(props.publicCompanies?.filter((company: any) => company.isPremium).map((company: any) => ({
                        value: company.id,
                        label: `${company.name} (ID: ${company.id})`,
                      })) || []),
                    ]}
                    disabled={!canEditRules}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <FormInputMultiCheckbox
                  name="weekdays"
                  control={control}
                  label={i18next.t('selector-weekdays')}
                  options={[
                    EPricingWeekDays.MON,
                    EPricingWeekDays.TUE,
                    EPricingWeekDays.WED,
                    EPricingWeekDays.THU,
                    EPricingWeekDays.FRI,
                    EPricingWeekDays.SAT,
                    EPricingWeekDays.SUN,
                  ].map(w => ({
                    label: i18next.t(`enums-EPricingWeekDays-${w}`),
                    value: w,
                  }))}
                  disabled={!canEditRules}
                />
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            {props.data.priceList.forLister ? (
              <ListerPriceTabs
                initialIndex={initialPricesTabSelected}
                tabs={[
                  {
                    key: 'rooms',
                    label: i18next.t('selector-lister-rooms'),
                    content: productsTabContent,
                    hasError: hasRoomErrors,
                  },
                  {
                    key: 'board',
                    label: i18next.t('selector-lister-board'),
                    content: bundlesTabContent,
                    hasError: hasBoardErrors,
                  },
                  {
                    key: 'amenities',
                    label: i18next.t('selector-lister-amenities'),
                    content: facilitiesTabContent,
                    hasError: Boolean(validationErrors.facilitiesPrices),
                  },
                  {
                    key: 'combinations',
                    label: i18next.t('selector-lister-combinations'),
                    content: listerCombinationsTabContent || (
                      <Typography variant="body2">
                        {i18next.t('selector-lister-combinations-empty')}
                      </Typography>
                    ),
                    hasError: hasCombinationErrors,
                  },
                ]}
              />
            ) : (
              <CustomTabs
                headers={[i18next.t('selector-products-tab'), i18next.t('selector-bundles-tab'), i18next.t('selector-facilities-tab')]}
                initialSelected={initialPricesTabSelected}
                icons={[
                  validationErrors.productsPrices ? <ErrorIcon /> : undefined,
                  validationErrors.bundlesPrices ? <ErrorIcon /> : undefined,
                  validationErrors.facilitiesPrices ? <ErrorIcon /> : undefined,
                ]}
                tabs={[productsTabContent, bundlesTabContent, facilitiesTabContent]}
              />
            )}
          </Grid>
          {canEdit && !props.data.priceList.forLister && (
            <Grid item xs={12}>
              {showValidationAlert(validationErrors)}
            </Grid>
          )}
          <Grid item xs={12}>
            {canEdit && (
              <Button
                sx={{ marginRight: 2 }}
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={(props.data.id > 0 && !isDirty && !switchesChanged) || isSubmitting || isValidating}
                onClick={async () => {
                  const valid = await trigger();
                  if (valid) {
                    handleSubmit(onSubmit)();
                  }
                }}
              >
                {i18next.t('selector-save')}
              </Button>
            )}
            {canEdit && !props.data.isBasePrice && props.data.id > 0 && (
              <PriceSelectorDeleteButton
                id={props.data.id}
                priceListId={props.data.priceList.id}
                spaceId={props.data.space.id}
                icon={false}
                onDeleted={props.onDeleted}
              />
            )}
            {canEdit && props.data.id > 0 && (
              <PriceSelectorCopyButton id={props.data.id} priceListId={props.data.priceList.id} spaceId={props.data.space.id} icon={false} />
            )}
          </Grid>
        </FormProvider>
      </Grid>
    </>
  );
}

/**
 * Price selector detail page wrapper that loads selector data.
 * Renders PriceSelectorForm for editing selector configuration and price rules.
 * Selectors define pricing conditions (e.g., room type, meal plan, occupancy).
 */
export default function PriceSelector(props: PriceSelectorProps) {
  const priceSelectorQuery = useQuery(PRICESELECTOR_VIEW_QUERY, {
    variables: { id: props.id },
  });
  const baseSelectorQuery = useQuery(PRICESELECTORBASE_PRICES_VIEW_QUERY, {
    variables: { priceListId: props.priceListId },
  });

  // Use props as fallback to eliminate waterfall — parent already knows spaceId/forLister
  const forLister = props.forLister ?? priceSelectorQuery.data?.viewPriceSelector?.priceList?.forLister;
  const spaceId = props.spaceId ?? priceSelectorQuery.data?.viewPriceSelector?.priceList?.space?.id;

  const productsQuery = useQuery(PRODUCTS_COMPONENTS_QUERY, {
    variables: { spaceId, where: { forLister: !!forLister } },
    skip: !spaceId,
    fetchPolicy: 'cache-first',
  });
  const productBundlesQuery = useQuery(PRODUCTBUNDLES_COMPONENTS_QUERY, {
    variables: { spaceId, where: { forLister: !!forLister } },
    skip: !spaceId,
    fetchPolicy: 'cache-first',
  });
  const facilitiesQuery = useQuery(FACILITIES_COMPONENTS_QUERY, {
    variables: { spaceId, where: { forLister: !!forLister } },
    skip: !spaceId,
    fetchPolicy: 'cache-first',
  });
  const taxTypesQuery = useQuery(TAXTYPES_LIST_FULL_QUERY, {
    variables: { spaceId: null },
    fetchPolicy: 'cache-first',
  });
  const needsPublicCompanies = !!(props.forLister ?? priceSelectorQuery.data?.viewPriceSelector?.priceList?.forLister);
  const { data: publicCompaniesData } = useQuery(PUBLIC_COMPANY_LIST_QUERY, {
    variables: { skip: 0, take: 200 },
    skip: !needsPublicCompanies,
    fetchPolicy: 'cache-first',
  });

  const criticalLoading =
    priceSelectorQuery.loading || baseSelectorQuery.loading || productsQuery.loading || productBundlesQuery.loading || facilitiesQuery.loading;
  const error = priceSelectorQuery.error || baseSelectorQuery.error;

  if (criticalLoading) return <CircularProgress />;
  else if (error) return <RedirectError err={error} />;
  else {
    return (
      <PriceSelectorForm
        data={priceSelectorQuery.data!.viewPriceSelector! as PriceSelectorListOutput}
        baseSelector={baseSelectorQuery.data!.viewBasePriceSelectorPrices! as PriceSelectorListOutput}
        products={(productsQuery.data!.listProducts.products || []) as ProductListOutput[]}
        bundles={(productBundlesQuery.data!.listProductBundles.bundles || []) as ProductBundleListOutput[]}
        facilities={(facilitiesQuery.data!.listFacilities.facilities || []) as FacilityListOutput[]}
        taxTypes={(taxTypesQuery.data?.listTaxTypes || []) as TaxTypeListOutput[]}
        publicCompanies={publicCompaniesData?.listPublicCompanies?.companies}
        inline={props.inline}
        onNavigateToSelector={props.onNavigateToSelector}
        onDeleted={props.onDeleted}
      />
    );
  }
}

/**
 * Price selector creation page for new selectors.
 * Uses PriceSelectorForm with empty initial data.
 */
export function PriceSelectorCreate(props: PriceSelectorCreateProps) {
  const priceListQuery = useQuery(PRICELIST_VIEW_QUERY, {
    variables: { id: props.priceListId },
    skip: !!props.priceList,
  });
  const baseSelectorQuery = useQuery(PRICESELECTORBASE_PRICES_VIEW_QUERY, {
    variables: { priceListId: props.priceListId },
  });

  // Use props as fallback to eliminate waterfall — parent already knows spaceId/forLister
  const resolvedPriceList = props.priceList || (priceListQuery.data?.viewPriceList as PriceListListOutput | undefined);
  const forLister = props.forLister ?? resolvedPriceList?.forLister;
  const spaceId = props.spaceId ?? resolvedPriceList?.space?.id;

  const productsQuery = useQuery(PRODUCTS_COMPONENTS_QUERY, {
    variables: { spaceId, where: { forLister: !!forLister } },
    skip: !spaceId,
    fetchPolicy: 'cache-first',
  });
  const productBundlesQuery = useQuery(PRODUCTBUNDLES_COMPONENTS_QUERY, {
    variables: { spaceId, where: { forLister: !!forLister } },
    skip: !spaceId,
    fetchPolicy: 'cache-first',
  });
  const facilitiesQuery = useQuery(FACILITIES_COMPONENTS_QUERY, {
    variables: { spaceId, where: { forLister: !!forLister } },
    skip: !spaceId,
    fetchPolicy: 'cache-first',
  });
  const taxTypesQuery = useQuery(TAXTYPES_LIST_FULL_QUERY, {
    variables: { spaceId: null },
    fetchPolicy: 'cache-first',
  });
  const needsPublicCompanies = !!(props.forLister ?? resolvedPriceList?.forLister);
  const { data: publicCompaniesData } = useQuery(PUBLIC_COMPANY_LIST_QUERY, {
    variables: { skip: 0, take: 200 },
    skip: !needsPublicCompanies,
    fetchPolicy: 'cache-first',
  });

  const loading = priceListQuery.loading || baseSelectorQuery.loading || productsQuery.loading || productBundlesQuery.loading || facilitiesQuery.loading;

  const error =
    priceListQuery.error ||
    baseSelectorQuery.error;

  if (loading) return <CircularProgress />;
  else if (!loading && error) return <RedirectError err={error} />;
  else {
    return (
      <PriceSelectorForm
        data={{
          id: -1,
          name: '',
          isBasePrice: false,
          includeMainRoomInPackage: false,
          priceList: resolvedPriceList!,
          prices: [],
          validity: [],
          weekdays: [],
          space: resolvedPriceList!.space as any,
        }}
        baseSelector={baseSelectorQuery.data!.viewBasePriceSelectorPrices! as PriceSelectorListOutput}
        products={(productsQuery.data!.listProducts.products || []) as ProductListOutput[]}
        bundles={(productBundlesQuery.data!.listProductBundles.bundles || []) as ProductBundleListOutput[]}
        facilities={(facilitiesQuery.data!.listFacilities.facilities || []) as FacilityListOutput[]}
        taxTypes={(taxTypesQuery.data?.listTaxTypes || []) as TaxTypeListOutput[]}
        publicCompanies={publicCompaniesData?.listPublicCompanies?.companies}
        inline={props.inline}
        onNavigateToSelector={props.onNavigateToSelector}
        onDeleted={props.onDeleted}
      />
    );
  }
}

interface PriceSelectorCopyButtonProps {
  id: number;
  priceListId: number;
  spaceId: number;
  icon: boolean;
}
export function PriceSelectorCopyButton(props: PriceSelectorCopyButtonProps) {
  const dispatch = useDispatch();

  const user = userSelector()!;
  const canEdit = canEditAdminSpaceId(user, props.spaceId);

  const [copyMutateFunction, { loading: copyMutateLoading }] = useMutation(COPY_PRICESELECTOR_MUTATION);

  const __do = async () => {
    if (!canEdit) return;
    try {
      await copyMutateFunction({
        variables: { id: props.id },
        update: cache => EVICT_PRICESELECTORS_QUERIES(cache),
        awaitRefetchQueries: true,
        refetchQueries: REFETCH_PRICESELECTORS_QUERIES(props.priceListId),
      });
      dispatchMessage(dispatch, i18next.t('selector-copied'));
    } catch (err) {
      dispatchException(dispatch, err);
    }
  };

  if (props.icon && !canEdit) return null;

  if (props.icon) {
    return (
      <IconButton disabled={!canEdit || copyMutateLoading} onClick={__do}>
        <Tooltip title={i18next.t('selector-copy')}>
          <ContentCopyIcon />
        </Tooltip>
      </IconButton>
    );
  } else {
    return (
      <Button
        sx={{ marginRight: 2 }}
        variant="contained"
        color="secondary"
        disabled={!canEdit || copyMutateLoading}
        startIcon={copyMutateLoading ? <CircularProgress size={24} /> : <ContentCopyIcon />}
        onClick={__do}
      >
        {i18next.t('selector-copy')}
      </Button>
    );
  }
}

interface PriceSelectorDeleteButtonProps {
  id: number;
  priceListId: number;
  spaceId: number;
  icon: boolean;
  onBeforeDelete?: () => void;
  onDeleted?: () => void;
}
export function PriceSelectorDeleteButton(props: PriceSelectorDeleteButtonProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user = userSelector()!;
  const canEdit = canEditAdminSpaceId(user, props.spaceId);

  const [deleteMutateFunction, { loading: deleteMutateLoading }] = useMutation(DELETE_PRICESELECTOR_MUTATION);

  if (props.icon && !canEdit) return null;

  return (
    <ConfirmationButton
      sx={{ marginRight: 2 }}
      disabled={!canEdit || deleteMutateLoading}
      icon={props.icon}
      {...(props.icon
        ? {}
        : {
          startIcon: deleteMutateLoading ? <CircularProgress size={24} /> : <DeleteIcon />,
          variant: 'contained',
          color: 'secondary',
        })}
      confirmationQuestion={i18next.t('selector-confirm-delete')}
      confirmationTitle={i18next.t('selector-confirm-delete-title')}
      onConfirm={async () => {
        if (!canEdit) return;
        try {
          // Close inline form before delete to prevent stale query refetch errors
          if (props.onBeforeDelete) {
            props.onBeforeDelete();
            // Small delay to allow React to unmount the inline component
            await new Promise(resolve => setTimeout(resolve, 50));
          }
          await deleteMutateFunction({
            variables: {
              id: props.id,
            },
            update: cache => EVICT_PRICESELECTORS_QUERIES(cache),
            awaitRefetchQueries: true,
            refetchQueries: REFETCH_PRICESELECTORS_QUERIES(props.priceListId),
          });
          if (props.onDeleted) {
            props.onDeleted();
          } else {
            navigate(`/pricelists/pricelists/${props.priceListId}`);
          }
          dispatchMessage(dispatch, i18next.t('selector-deleted'));
        } catch (err) {
          dispatchException(dispatch, err);
        }
      }}
    >
      {props.icon && (deleteMutateLoading ? <CircularProgress size={24} /> : <DeleteIcon />)}
      {!props.icon && i18next.t('selector-delete')}
    </ConfirmationButton>
  );
}
